import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { isValidIban } from "@/lib/iban";
import { useToast } from "@/hooks/use-toast";
import {
  OWNER_FEE_VAT_RATE,
  DEFAULT_MANAGEMENT_FEE,
  isVacantStatus,
} from "@/lib/owner-vacancy";
import {
  isOrderPriceable,
  isVacant,
  objectsFromGroupedRows,
  orderFeeTotals,
  totalNetFee,
  uniformFeePerRoom,
  type GroupedObjectRow,
  type OnboardingObject,
} from "@/lib/owner-onboarding-objects";
import {
  applyCorrectionLocally,
  correctionRefusal,
  type CorrectionRefusal,
} from "@/lib/owner-property-correction";
import { useLanguage, type Language } from "@/contexts/LanguageContext";
import {
  Check,
  CheckCircle2,
  ChevronLeft,
  Pencil,
  FileText,
  Lock,
  Download,
  ArrowRight,
  XCircle,
} from "lucide-react";
import SignaturePad from "react-signature-canvas";
import { formatAddress } from "@/lib/v2-format";
import { BrandWordmark } from "@/components/BrandWordmark";

// ── i18n ───────────────────────────────────────────────────────────────────

export const T = {
  de: {
    /** Which dictionary this is. Cheaper and safer than inferring the
        language by comparing a translated string to a literal. */
    lang: "de" as const,
    closeLabel: "Schließen",
    leaveTitle: "Onboarding verlassen?",
    leaveDescription:
      "Ihre Unterschrift wird nur für diese Sitzung gespeichert. Wenn Sie das Onboarding verlassen, wird sie verworfen.",
    leaveCancel: "Weiter bearbeiten",
    leaveConfirm: "Verlassen",
    yourDocumentsLabel: "Ihre Dokumente",
    mailOnTheWay: "E-Mail unterwegs!",
    loadingLabel: "Wird geladen…",
    step: "Schritt",
    of: "von",
    back: "Zurück",
    railLede:
      "Der Ablauf, den Sie durchlaufen, um den Verwaltungsvertrag zu unterschreiben.",
    whatToExpect: "Was Sie erwartet",
    servicesLabel: "Leistungsumfang gemäß § 3 AGB",
    trustSignals: [
      "Sicher & verschlüsselt",
      "5 Min. Onboarding",
      "Juristisch geprüft",
    ],
    bankSectionLabel: "Mieteingangskonto",
    bankSectionNote:
      "Auf dieses Konto überweisen wir Ihnen monatlich die Mieteinnahmen abzüglich unserer Verwaltungsgebühr. Es wird nichts von Ihrem Konto abgebucht.",
    signaturePlaceholder: "Zum Unterschreiben tippen",
    vatShare: "davon 19% MwSt.",
    vacantNoFee: "keine Gebühr bei Leerstand",
    vollmachtConsentPre: "Ich habe die ",
    vollmachtConsentLink: "Verwaltervollmacht",
    vollmachtConsentPost: " gelesen und erteile diese.",
    vollmachtConsentRequired: "Bitte bestätigen Sie die Verwaltervollmacht.",
    // Step 1
    welcomeTag: "Willkommen bei coliviq",
    welcomeHeadline: "coliviq WG-Immobilienverwaltung",
    welcomeBody: (name: string) =>
      name
        ? `Hallo ${name}, schön, dass Sie dabei sind. Wir übernehmen die komplette Vermietung und Verwaltung Ihrer Immobilie — digital, transparent und stressfrei.`
        : `Schön, dass Sie dabei sind. Wir übernehmen die komplette Vermietung und Verwaltung Ihrer Immobilie — digital, transparent und stressfrei.`,
    welcomeSub:
      "Bitte prüfen Sie Ihre Daten und erteilen Sie Ihren verbindlichen Verwaltungsauftrag.",
    featureVetted: "Geprüfte Mieter",
    featureVettedSub: "Inkl. Bonitätsprüfung",
    featureRent: "Pünktliche Miete",
    featureRentSub: "Zahlung & Mahnwesen",
    featureTransparency: "Volle Transparenz",
    featureTransparencySub: "Echtzeit-Überblick",
    getStarted: "Jetzt starten",
    // Step 2
    step2Sub:
      "Dies sind die bei uns gespeicherten Daten. Bitte prüfen Sie diese auf Richtigkeit.",
    ownerLabel: "Eigentümer",
    propertyTitleField: "Name der Immobilie",
    propertyLabel: "Immobilie zum Verwalten",
    // Numbered rows of a grouped invite: "Immobilie 1", not "Immobilie zum Verwalten 1".
    propertyRowLabel: "Immobilie",
    propertySaveSigned:
      "Diese Immobilie wurde bereits unterzeichnet und lässt sich hier nicht mehr ändern. Bitte melden Sie sich bei uns, damit wir die Unterlagen korrigieren.",
    propertySaveShared:
      "Diese Immobilie gehört mehreren Eigentümern, deshalb können wir sie hier nicht ändern. Bitte melden Sie sich bei uns.",
    propertySaveInvite:
      "Dieser Einladungslink ist nicht mehr gültig. Bitte laden Sie die Seite neu.",
    houseNumber: "Hausnummer",
    zipCode: "PLZ",
    cityLabel: "Stadt",
    street: "Straße",
    propertySaveFailed:
      "Die Immobilie konnte nicht gespeichert werden. Bitte versuchen Sie es erneut oder melden Sie sich bei uns.",
    editLabel: "Bearbeiten",
    doneLabel: "Speichern",
    fullName: "Vollständiger Name",
    address: "Adresse",
    email: "E-Mail",
    phone: "Telefon",
    livingArea: "Wohnfläche",
    rooms: "Zimmer",
    rentalStatus: "Vermietungsstatus",
    statusRented: "Vermietet",
    statusVacant: "Leerstand",
    saveBeforeContinue:
      "Bitte speichern Sie Ihre Änderungen bevor Sie fortfahren.",
    confirmContinue: "Bestätigen & weiter",
    // Step 3
    step3Headline: "Was wir für Sie erledigen",
    step3Sub: "Full-Service-Verwaltung gemäß § 3 der AGBs.",
    services: [
      {
        icon: "📋",
        title: "Inserate & Besichtigungen",
        sub: "Professionelles Marketing und Koordination aller Termine.",
      },
      {
        icon: "✅",
        title: "Mieterauswahl & Bonitätsprüfung",
        sub: "Sorgfältige Auswahl.",
      },
      {
        icon: "📄",
        title: "Mietvertrag",
        sub: "Abschluss rechtssicherer Verträge.",
      },
      {
        icon: "🔑",
        title: "Einzug & Übergabeprotokoll",
        sub: "Vollständige Dokumentation bei jedem Ein- und Auszug.",
      },
      {
        icon: "💳",
        title: "Mieteingangskontrolle & Mahnwesen",
        sub: "Überwachung aller Zahlungseingänge und Mahnungen.",
      },
      {
        icon: "🔄",
        title: "Mieterwechsel & Kommunikation",
        sub: "Ein einziger Ansprechpartner für Ihre Mieter.",
      },
      {
        icon: "🔧",
        title: "Instandhaltungsmanagement",
        sub: "Koordination von Reparaturen und Handwerkern.",
      },
    ],
    platformIncluded: "Inklusive",
    platformTitle: "Zugang zur coliviq-Verwaltungsplattform",
    platformSub:
      "Echtzeit-Überblick: Belegung, Zahlungen und Dokumentation — jederzeit verfügbar.",
    managementFee: "Verwaltungsgebühr",
    perMonth: "/Monat",
    perRoomMonth: "€ netto / Zimmer / Monat",
    roomsRow: (n: number, total: string) =>
      `× ${n} Zimmer = ${total} € / Monat`,
    vatNote: (gross: string) => `zzgl. 19% MwSt. — ${gross} inkl.`,
    noVacancyFee: "Keine Gebühr bei Leerstand",
    noRelettingFee: "Keine Neuvermietungsgebühr",
    sslNote: "Zahlung erst nach Bestätigung · SSL-verschlüsselt",
    step3CheckLabel:
      "Ich habe die AGB und Verwaltervollmacht gelesen und akzeptiere diese.",
    // The approved mockup labels this simply "Bestätigen" -- the step is a
    // confirmation of what coliviq does, not a navigation hint to the next
    // screen, and naming the destination here duplicated the progress bar.
    continueToOrder: "Bestätigen",
    docModalTitle: "Vertragsunterlagen herunterladen",
    docVerwaltervollmacht: "Verwaltervollmacht",
    docAgb: "AGB Verwaltungsvertrag",
    docDownload: "Herunterladen",
    docNotAvailable: "Noch nicht hochgeladen",
    required: "Pflichtfeld",
    ibanInvalid: "Diese IBAN ist ungültig. Bitte prüfen Sie die Eingabe.",
    namePlaceholder: "Vor- und Nachname",
    // Step 5
    // NOTE: the step5*/step4* key names are historical. Vollmacht is now
    // step 4 and the order step 5, so step5Tag renders on step 4 and
    // step4Tag on step 5. The keys were left alone to keep this diff to
    // the reorder itself.
    step5Tag: "Schritt 4 — Vollmacht",
    // The step signs every contract document, not only the mandate -- its
    // own subline says "alle Vertragsunterlagen" and an AGB attachment
    // rides along. The prototype titles it accordingly.
    step5Headline: "Dokumente unterzeichnen",
    step5Sub:
      "Bitte lesen und unterzeichnen Sie alle Vertragsunterlagen. Ihre Unterschrift gilt für alle angezeigten Dokumente.",
    step5DocFilename: "Vollmacht_Hausverwaltung.pdf",
    step5TemplateIncomplete:
      "Die Vorlage der Verwaltervollmacht enthält noch nicht für jede Liegenschaft dieser Einladung ein Feld. Bitte kontaktieren Sie coliviq — die Unterzeichnung ist bis dahin nicht möglich.",
    step5Principal: "Vollmachtgeber",
    step5Agent: "Bevollmächtigter",
    step5Property: "Bevollmächtigtes Objekt",
    step5Properties: "Bevollmächtigte Objekte",
    propertiesLabel: (n: number) => `${n} Objekte`,
    propertyCount: (n: number) => `${n} Immobilien`,
    groupedLookupFailed:
      "Die Objekte dieser Einladung konnten nicht geladen werden. Bitte laden Sie die Seite neu oder melden Sie sich bei uns.",
    feeNotPriceable:
      "Für diese Einladung ist keine Zimmeranzahl hinterlegt, daher lässt sich die monatliche Gebühr nicht berechnen. Bitte melden Sie sich bei uns — wir ergänzen das und senden Ihnen die Einladung erneut.",
    step5AuthTitle: "Umfang der Vollmacht",
    step5AuthLines: [
      "Der Vollmachtgeber ermächtigt Coliviq GmbH, folgende Handlungen vorzunehmen:",
      "1. Abschluss, Änderung und Kündigung von Mietverträgen",
      "2. Entgegennahme und Verwaltung von Miet- und Nebenkosten",
      "3. Beauftragung von Instandhaltungs- und Reparaturmaßnahmen",
      "4. Korrespondenz mit Mietern, Behörden und Dienstleistern",
      "5. Erstellung von Betriebskostenabrechnungen",
      "6. Durchführung von Besichtigungen und Mieterauswahl",
    ],
    step5SignatureLabel: "Ihre Unterschrift",
    step5SignatureNote:
      "Diese Unterschrift wird auf alle Dokumente angewendet.",
    step5ClearSignature: "Löschen",
    step5Submit: "Alle Dokumente bestätigen",
    step5Submitting: "Wird übermittelt...",
    step5SignatureRequired: "Bitte unterzeichnen Sie die Dokumente.",
    step5LoadingPreview: "Dokumente werden vorbereitet…",
    step5NoTemplates: "Keine Dokumente konfiguriert.",
    step5DocOf: (n: number, total: number) => `Dokument ${n} von ${total}`,
    step5OpenInTab: "Im neuen Tab öffnen ↗",
    step5NoPreview: "Vorschau nicht verfügbar",
    step5PreviewNote:
      "Das Dokument ist mit Ihren gespeicherten Daten vorausgefüllt.",
    // Step 6 email sending
    sendingContracts: "Verträge werden gesendet…",
    // Step 4 (was 4)
    step4Tag: "Schritt 5 — Auftrag",
    step4Headline: "Vertrag & Zahlung",
    step4Sub:
      "Bitte prüfen Sie die wichtigsten Vertragskonditionen und erteilen Sie Ihren Auftrag.",
    contractTerms: [],
    downloadPdf: "Vollständigen Vertrag als PDF herunterladen",
    yourSignature: "Ihre Unterschrift",
    clearSignature: "Unterschrift löschen",
    accountHolder: "Kontoinhaber",
    yourOrder: "Ihr Auftrag",
    propertyOrderLabel: "Immobilie",
    contractStart: "Vertragsbeginn",
    managementFeeOrder: "Verwaltungsgebühr",
    netLabel: "netto",
    monthlyTotal: "Monatlicher Gesamtbetrag",
    vatIncl: "inkl. 19% MwSt.",
    check1: (
      <>
        Ich habe die <strong>Widerrufsbelehrung</strong> gelesen und akzeptiere
        den <strong>Verwaltungsvertrag</strong> und den verbindlichen,
        kostenpflichtigen Auftrag.
      </>
    ),
    check2: (
      <>
        Ich habe die <strong>AGB Verwaltungsvertrag</strong> und die{" "}
        <strong>Verwaltervollmacht</strong> gelesen und akzeptiere diese.
      </>
    ),
    payNote: "🔒 Zahlung erst nach Bestätigung · SSL-verschlüsselt",
    submitBtn: "Auftrag verbindlich erteilen",
    processing: "Wird verarbeitet...",
    // Step 5
    thankyouHeadline: (name: string) => `Vielen Dank, ${name}!`,
    thankyouSub: "Ihre Bestellung ist eingegangen. Wir legen sofort los.",
    copyTitle: "Kopieren",
    copiedLabel: "Kopiert!",
    thankyouObjects: (n: number) =>
      `${n} Immobilien sind in diesem Auftrag enthalten.`,
    referenceLabel: "Referenznummer",
    nextStepsLabel: "Wie geht es weiter",
    nextSteps: [
      {
        n: 1,
        title: "Vertrag per E-Mail",
        sub: "Sie erhalten den unterzeichneten Vertrag in Kürze.",
      },
      {
        n: 2,
        title: "Plattformzugang einrichten",
        sub: "Wir richten Ihr persönliches Konto ein.",
      },
      {
        n: 3,
        title: "Vermarktung beginnt",
        sub: "coliviq startet die Vermarktung Ihrer Immobilie.",
      },
    ],
    goToPlatform: "Zur coliviq-Plattform →",
    // Set password modal
    pwdTitle: "Passwort festlegen",
    pwdSubtitle:
      "Legen Sie ein Passwort für Ihren coliviq-Zugang fest, um auf Ihr Dashboard zuzugreifen.",
    pwdLabel: "Passwort",
    pwdConfirmLabel: "Passwort bestätigen",
    pwdPlaceholder: "Mindestens 8 Zeichen",
    pwdSubmit: "Passwort festlegen & loslegen",
    pwdSettingUp: "Wird eingerichtet…",
    pwdTooShort: "Das Passwort muss mindestens 8 Zeichen lang sein.",
    pwdMismatch: "Die Passwörter stimmen nicht überein.",
    pwdSetFailed:
      "Passwort konnte nicht festgelegt werden. Bitte versuchen Sie es erneut.",
    // Errors
    invalidLink: "Ungültiger Link.",
    linkExpiredOrInvalid: "Dieser Link ist ungültig oder abgelaufen.",
    alreadyCompleted:
      "Sie haben das Onboarding bereits abgeschlossen. Prüfen Sie Ihre E-Mail für Ihren Anmeldelink.",
    invitationExpired:
      "Dieser Einladungslink ist abgelaufen. Bitte wenden Sie sich an Ihren coliviq-Administrator.",
    linkUnavailable: "Link nicht verfügbar",
    submissionFailed: "Übermittlung fehlgeschlagen",
    dateLocale: "de-DE",
  },
  en: {
    lang: "en" as const,
    closeLabel: "Close",
    leaveTitle: "Leave onboarding?",
    leaveDescription:
      "Your signature is saved only for this session. Leaving onboarding will discard it.",
    leaveCancel: "Keep editing",
    leaveConfirm: "Leave onboarding",
    yourDocumentsLabel: "Your Documents",
    mailOnTheWay: "Check your inbox!",
    loadingLabel: "Loading…",
    step: "Step",
    of: "of",
    back: "Back",
    railLede: "The steps you go through to sign the management contract.",
    whatToExpect: "What to expect",
    servicesLabel: "Scope of services under § 3 of the AGB",
    trustSignals: [
      "Secure & encrypted",
      "5 min onboarding",
      "Legally reviewed",
    ],
    bankSectionLabel: "Rent receipt account",
    bankSectionNote:
      "We transfer your rental income to this account every month, less our management fee. Nothing is ever debited from your account.",
    signaturePlaceholder: "Tap to sign",
    vatShare: "of which 19% VAT",
    vacantNoFee: "no fee while vacant",
    vollmachtConsentPre: "I have read the ",
    vollmachtConsentLink: "power of attorney",
    vollmachtConsentPost: " and hereby grant it.",
    vollmachtConsentRequired: "Please confirm the power of attorney.",
    welcomeTag: "Welcome to coliviq",
    welcomeHeadline: "coliviq Co-Living Management",
    welcomeBody: (name: string) =>
      name
        ? `Hello ${name}, glad to have you here. We take care of the complete letting and management of your property — digital, transparent and stress-free.`
        : `Glad to have you here. We take care of the complete letting and management of your property — digital, transparent and stress-free.`,
    welcomeSub:
      "Please review your property details and place your binding order for management.",
    featureVetted: "Vetted tenants",
    featureVettedSub: "Incl. credit check",
    featureRent: "On-time rent",
    featureRentSub: "Payments & dunning",
    featureTransparency: "Full transparency",
    featureTransparencySub: "Real-time platform",
    getStarted: "Get started",
    step2Sub:
      "These are the details we have on file for you. Please check them for accuracy.",
    ownerLabel: "Owner",
    propertyTitleField: "Property name",
    propertyLabel: "Property to manage",
    propertyRowLabel: "Property",
    propertySaveSigned:
      "This property has already been signed for and cannot be changed here. Please contact us so we can correct the documents.",
    propertySaveShared:
      "This property has more than one owner, so we cannot change it here. Please contact us.",
    propertySaveInvite:
      "This invitation link is no longer valid. Please reload the page.",
    houseNumber: "House number",
    zipCode: "Postcode",
    cityLabel: "City",
    street: "Street",
    propertySaveFailed:
      "The property could not be saved. Please try again, or contact us if it keeps happening.",
    editLabel: "Edit",
    doneLabel: "Done",
    fullName: "Full name",
    address: "Address",
    email: "Email",
    phone: "Phone",
    livingArea: "Living area",
    rooms: "Rooms",
    rentalStatus: "Rental status",
    statusRented: "Rented",
    statusVacant: "Vacant",
    saveBeforeContinue: "Please save your changes before continuing.",
    confirmContinue: "Confirm & continue",
    step3Headline: "What we handle for you",
    step3Sub: "Full-service management under § 3 of the management agreement.",
    services: [
      {
        icon: "📋",
        title: "Listings & Viewings",
        sub: "Professional marketing and coordination of all appointments.",
      },
      {
        icon: "✅",
        title: "Tenant Selection & Credit Check",
        sub: "Careful selection.",
      },
      {
        icon: "📄",
        title: "Lease Agreement",
        sub: "Completion of legally sound contracts.",
      },
      {
        icon: "🔑",
        title: "Move-in & Handover Protocol",
        sub: "Complete documentation for every move-in and move-out.",
      },
      {
        icon: "💳",
        title: "Rent Collection & Dunning",
        sub: "Monitoring of all incoming payments and reminders.",
      },
      {
        icon: "🔄",
        title: "Tenant Turnover & Communication",
        sub: "A single point of contact for your tenants.",
      },
      {
        icon: "🔧",
        title: "Maintenance Management",
        sub: "Coordination of repairs and tradespeople.",
      },
    ],
    platformIncluded: "Included",
    platformTitle: "Access to the coliviq Management Platform",
    platformSub:
      "Real-time overview: occupancy, payments and documentation — available anytime.",
    managementFee: "Management fee",
    perMonth: "/month",
    perRoomMonth: "€ net / room / month",
    roomsRow: (n: number, total: string) => `× ${n} rooms = ${total} € / month`,
    vatNote: (gross: string) => `plus 19% VAT — ${gross} incl.`,
    noVacancyFee: "No fee during vacancy",
    noRelettingFee: "No re-letting fee",
    sslNote: "You only pay after confirmation · SSL-encrypted",
    step3CheckLabel: "I have read and accept the AGB and Verwaltervollmacht.",
    continueToOrder: "Confirm",
    docModalTitle: "Download contract documents",
    docVerwaltervollmacht: "Verwaltervollmacht (Power of Attorney)",
    docAgb: "AGB Verwaltungsvertrag (Terms & Conditions)",
    docDownload: "Download",
    docNotAvailable: "Not yet uploaded",
    required: "Required",
    ibanInvalid: "This IBAN is not valid. Please check it.",
    namePlaceholder: "Full name",
    step5Tag: "Step 4 — Power of attorney",
    step5Headline: "Sign documents",
    step5Sub:
      "Please review and digitally sign the power of attorney. You authorise Coliviq GmbH to act on your behalf.",
    step5DocFilename: "Vollmacht_Hausverwaltung.pdf",
    step5TemplateIncomplete:
      "The Verwaltervollmacht template does not yet have a field for every property in this invitation. Please contact coliviq — signing is not possible until then.",
    step5Principal: "Principal",
    step5Agent: "Agent",
    step5Property: "Managed Property",
    step5Properties: "Managed Properties",
    propertiesLabel: (n: number) => `${n} properties`,
    propertyCount: (n: number) => `${n} properties`,
    groupedLookupFailed:
      "The properties on this invitation could not be loaded. Please reload the page, or contact us if it keeps happening.",
    feeNotPriceable:
      "This invitation has no room count on file, so the monthly fee cannot be calculated. Please contact us — we will add it and send you the invitation again.",
    step5AuthTitle: "Scope of Authority",
    step5AuthLines: [
      "The principal authorises Coliviq GmbH to:",
      "1. Enter, modify and terminate tenancy agreements",
      "2. Receive and manage rental income",
      "3. Commission maintenance and repair work",
      "4. Correspond with tenants, authorities and service providers",
      "5. Prepare utility cost statements",
      "6. Conduct viewings and select tenants",
    ],
    step5SignatureLabel: "Your Signature",
    step5SignatureNote: "This signature will be applied to all documents.",
    step5ClearSignature: "Clear",
    step5Submit: "Confirm all documents",
    step5Submitting: "Submitting...",
    step5SignatureRequired: "Please sign the documents.",
    step5LoadingPreview: "Preparing documents…",
    step5NoTemplates: "No documents configured.",
    step5DocOf: (n: number, total: number) => `Document ${n} of ${total}`,
    step5OpenInTab: "Open in new tab ↗",
    step5NoPreview: "Preview not available",
    step5PreviewNote: "The document is pre-filled with your stored details.",
    sendingContracts: "Sending contracts…",
    step4Tag: "Step 5 — Order",
    step4Headline: "Contract & payment",
    step4Sub: "Please review the key contract terms and complete your order.",
    contractTerms: [],
    downloadPdf: "Download the full contract as a PDF",
    yourSignature: "Your signature",
    clearSignature: "Clear signature",
    accountHolder: "Account holder",
    yourOrder: "Your order",
    propertyOrderLabel: "Property",
    contractStart: "Contract start",
    managementFeeOrder: "Management fee",
    netLabel: "net",
    monthlyTotal: "Monthly total",
    vatIncl: "incl. 19% VAT",
    check1: (
      <>
        I have read the <strong>withdrawal policy</strong> and accept the{" "}
        <strong>management agreement</strong> and the binding, paid order.
      </>
    ),
    check2: (
      <>
        I have read and accept the <strong>AGB Verwaltungsvertrag</strong> and{" "}
        <strong>Verwaltervollmacht</strong>.
      </>
    ),
    payNote: "🔒 You only pay after confirmation · SSL-encrypted",
    submitBtn: "Place binding paid order →",
    processing: "Processing...",
    thankyouHeadline: (name: string) => `Thank you, ${name}!`,
    thankyouSub:
      "Your order has been received. We'll get started for you right away.",
    copyTitle: "Copy",
    copiedLabel: "Copied!",
    thankyouObjects: (n: number) =>
      `${n} properties are covered by this order.`,
    referenceLabel: "Reference number",
    nextStepsLabel: "What happens next",
    nextSteps: [
      {
        n: 1,
        title: "Contract by email",
        sub: "You'll receive the signed contract shortly.",
      },
      {
        n: 2,
        title: "Set up platform access",
        sub: "We'll set up your personal account.",
      },
      {
        n: 3,
        title: "Marketing begins",
        sub: "coliviq starts marketing your property.",
      },
    ],
    goToPlatform: "Go to the coliviq platform →",
    // Set password modal
    pwdTitle: "Set your password",
    pwdSubtitle:
      "Choose a password for your coliviq account to access your dashboard.",
    pwdLabel: "Password",
    pwdConfirmLabel: "Confirm password",
    pwdPlaceholder: "At least 8 characters",
    pwdSubmit: "Set password & continue",
    pwdSettingUp: "Setting up…",
    pwdTooShort: "Password must be at least 8 characters.",
    pwdMismatch: "Passwords do not match.",
    pwdSetFailed: "Could not set your password. Please try again.",
    invalidLink: "Invalid link.",
    linkExpiredOrInvalid: "This link is invalid or has expired.",
    alreadyCompleted:
      "You've already completed onboarding. Check your email for your login link.",
    invitationExpired:
      "This invitation link has expired. Please contact your coliviq administrator.",
    linkUnavailable: "Link unavailable",
    submissionFailed: "Submission failed",
    dateLocale: "en-GB",
  },
} as const;

// ── Types ──────────────────────────────────────────────────────────────────

interface TokenData {
  id: string;
  token: string;
  email: string;
  property_id: number;
  prefilled_name: string | null;
  prefilled_phone: string | null;
  prefilled_bank_holder: string | null;
  prefilled_iban: string | null;
  prefilled_bic: string | null;
  prefilled_address: string | null;
  prefilled_fee_amount: number | null;
  prefilled_fee_model: string | null;
  prefilled_rooms: number | null;
  /**
   * Derived from the apartment by get_owner_onboarding_token, not snapshotted
   * onto the invite like the prefilled_* fields. Optional so the page still
   * type-checks against a deployment running the older lookup.
   */
  living_area?: number | null;
  tenancy_status?: string | null;
  status: string;
  expires_at: string;
}

interface PropertyData {
  id: string;
  title: string;
  address: string | null;
  city: string | null;
  zip_code: string | null;
  // The structured street columns. These exist on `properties` -- unlike
  // rooms / living_area / tenancy_status, which #1912 had to remove -- and
  // the editor below writes them rather than the composed `address`.
  street_name: string | null;
  street_number: string | null;
}

interface FormState {
  name: string;
  address: string;
  phone: string;
  bank_holder: string;
  iban: string;
  bic: string;
  payment_method: "sepa" | "credit_card";
  card_expiry: string;
  card_cvc: string;
  agb_accepted: boolean;
  docs_accepted: boolean;
  withdrawal_policy_accepted: boolean;
  management_agreement_accepted: boolean;
}

/** Page of the AGB PDF that holds the Widerrufsbelehrung (the last page). */
const AGB_WITHDRAWAL_PAGE = 7;

interface PlatformDoc {
  document_key: "verwaltervollmacht" | "agb";
  file_url: string | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function generateReference(): string {
  const year = new Date().getFullYear();
  const num = Math.floor(10000 + Math.random() * 90000);
  return `CLQ-${year}-${num}`;
}

function formatIban(val: string): string {
  return val
    .replace(/\s/g, "")
    .replace(/(.{4})/g, "$1 ")
    .trim()
    .toUpperCase();
}

// ── Progress bar ───────────────────────────────────────────────────────────

/**
 * "328,44 €" for a German owner, "328.44 €" for an English one.
 *
 * Totals always show cents; a headline rate passes `exact` so a whole-euro
 * tariff reads "69 €" rather than "69,00 €".
 */
function fmtEuro(
  value: number,
  tx: typeof T.de | typeof T.en,
  { exact = false }: { exact?: boolean } = {}
): string {
  return `${value.toLocaleString(tx.dateLocale, {
    minimumFractionDigits: exact && Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  })} €`;
}

// ── Progress + shell ──────────────────────────────────────────────────────

// Vollmacht (4) comes before Auftrag (5). The owner reads and signs the power
// of attorney first, but that signature is held in the browser — nothing is
// transmitted and no contract exists until the binding order in step 5, so the
// Widerrufsbelehrung still precedes anything leaving the page.
const STEP_LABELS_DE = [
  "Willkommen",
  "Ihre Daten",
  "Leistungen",
  "Vollmacht",
  "Auftrag",
  "Fertig",
];

const STEP_LABELS_EN = [
  "Welcome",
  "Your data",
  "Services",
  "Power of attorney",
  "Order",
  "Ready",
];

/**
 * Rail copy. These were tiny sub-labels under the old circle stepper; the rail
 * turns them into a column the owner reads for the whole flow, so German text
 * gets the room to say what each step actually asks for.
 */
/** Owner onboarding is German-only, independently of the app language.
 * English copy is deliberately retained for parity checks and future reuse. */
export function pickDictionary(_language: Language): typeof T.de {
  return T.de;
}

export const STEP_DESCS_DE = [
  "Begrüßung & Nutzenversprechen",
  "Eigentümer- & Objektdaten prüfen",
  "Leistungsumfang & Gebühr",
  "Vollmacht-Dokumente signieren",
  "Vertrag, Zahlung & Unterschrift",
  "Bestätigung & Plattformzugang",
];

export const STEP_DESCS_EN = [
  "Welcome & what you get",
  "Check owner and property data",
  "Scope of services & fee",
  "Sign the power of attorney",
  "Contract, payment & signature",
  "Confirmation & platform access",
];

function ProgressBar({ step, total }: { step: number; total: number }) {
  // One thin bar per step rather than numbered circles — no labels, so it
  // stays readable at six steps on a phone.
  return (
    <div className="flex items-center gap-1.5" aria-hidden="true">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`h-[3px] flex-1 rounded-full transition-colors ${
            i <= step - 1 ? "bg-[#0F4C4A]" : "bg-[#EFEBE3]"
          }`}
        />
      ))}
    </div>
  );
}

/* ── Prototype primitives ──────────────────────────────────────────────────
   The stage prototype builds every step out of four shapes: a small uppercase
   section label, read-only "field" boxes, hairline-separated rows, and one
   pill CTA on a row with a plain Zurück link. Steps 1-3 were already rebuilt
   that way; these give steps 4 and 5 the same vocabulary instead of the
   nested grey cards they inherited from the pre-prototype design. */

/** Small uppercase eyebrow that opens a section. */
function SectionLabel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={`text-[11px] font-semibold tracking-[0.12em] uppercase text-[#7A8886] ${className}`}
    >
      {children}
    </p>
  );
}

/** A row separated from the next by a warm hairline, as every list in the
    prototype is. The last row drops its rule so a section never ends on one. */
function HairlineRow({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`py-3 border-b border-[#EFEBE3] last:border-b-0 ${className}`}
    >
      {children}
    </div>
  );
}

/** Label left, value right — the shape of the order summary. */
function SummaryRow({
  label,
  value,
  strong = false,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  strong?: boolean;
}) {
  return (
    <HairlineRow className="flex justify-between items-baseline gap-4 text-[13.5px]">
      <span
        className={strong ? "font-semibold text-[#06201F]" : "text-[#6F7D7B]"}
      >
        {label}
      </span>
      <span
        className={`text-right tabular-nums ${strong ? "font-semibold text-[#06201F]" : "text-[#06201F]"}`}
      >
        {value}
      </span>
    </HairlineRow>
  );
}

/** A consent tick. Rendered as a label so the whole line toggles it, and kept
    inline rather than in a card: these are contract consents, and boxing them
    made them read as optional extras next to the order. */
function ConsentRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer group">
      <span className="relative mt-0.5 h-4 w-4 shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer block h-4 w-4 appearance-none rounded-[4px] border-[1.5px] border-[#CFCAC0] bg-white checked:border-[#0F4C4A] checked:bg-[#0F4C4A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F4C4A] cursor-pointer"
        />
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className="pointer-events-none absolute inset-0 hidden h-4 w-4 text-white peer-checked:block"
        >
          <path
            d="m3.5 8 3 3 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="text-[13px] leading-relaxed text-[#5B6B69]">
        {children}
      </span>
    </label>
  );
}

/** Zurück on the left, one pill CTA on the right. */
function CardActions({
  backLabel,
  onBack,
  children,
}: {
  backLabel?: string;
  onBack?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 mt-8">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-[13.5px] text-[#5B6B69] hover:text-[#0F4C4A] transition-colors"
        >
          {backLabel}
        </button>
      ) : (
        <span />
      )}
      {children}
    </div>
  );
}

/**
 * The stage rail at tablet and below.
 *
 * The rail is the only thing telling an owner where they are in a six-step
 * contract flow, so it must not simply disappear off desktop — it becomes a
 * horizontally scrollable strip instead. Steps are not clickable here either.
 */
function StageStrip({ step, labels }: { step: number; labels: string[] }) {
  const activeRef = React.useRef<HTMLLIElement>(null);

  // Keep the current stage in view as the owner advances; a strip that scrolls
  // off to the left stops answering "where am I".
  React.useEffect(() => {
    activeRef.current?.scrollIntoView({
      inline: "center",
      block: "nearest",
      behavior: "smooth",
    });
  }, [step]);

  return (
    <ol className="flex gap-1.5 overflow-x-auto px-5 pb-2.5 -mb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {labels.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <li
            key={label}
            ref={active ? activeRef : undefined}
            aria-current={active ? "step" : undefined}
            className={`flex items-center gap-1.5 shrink-0 rounded-full border px-2.5 py-1 ${
              active
                ? "border-[#0F4C4A] bg-[#0F4C4A] text-white"
                : "border-[#EAE4D8] bg-white text-[#7A8886]"
            }`}
          >
            <span
              className={`w-[18px] h-[18px] shrink-0 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                active
                  ? "bg-white/20 text-white"
                  : done
                    ? "bg-[#CFE4E2] text-[#0F4C4A]"
                    : "bg-[#EAE4D8] text-[#7A8886]"
              }`}
            >
              {n}
            </span>
            <span className="text-[12px] font-semibold whitespace-nowrap">
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Shell({
  step,
  total,
  hideChrome = false,
  objectCount = 1,
  onBack,
  tx,
  children,
}: {
  step: number;
  total: number;
  hideChrome?: boolean;
  objectCount?: number;
  onBack?: () => void;
  tx: typeof T.de | typeof T.en;
  children: React.ReactNode;
}) {
  const isDe = tx.lang === "de";
  const railLabels = isDe ? STEP_LABELS_DE : STEP_LABELS_EN;
  const railDescs = isDe ? STEP_DESCS_DE : STEP_DESCS_EN;

  return (
    <div className="min-h-screen bg-[#F7F3EC] lg:flex">
      {/* Stage rail. Shows where the owner is in the flow; the steps are not
          clickable, because skipping ahead in a contract flow would let them
          reach the order without the documents they are ordering against. */}
      <aside className="hidden lg:flex lg:flex-col w-[260px] sticky top-0 h-screen overflow-y-auto shrink-0 border-r border-[#EAE4D8] px-6 py-8">
        <BrandWordmark className="h-8 w-auto self-start mb-5" />
        <h1 className="text-base font-bold text-[#0F4C4A] tracking-tight mb-1.5">
          {isDe ? "Owner-Onboarding" : "Owner onboarding"}
          {objectCount > 1 && ` · ${tx.propertyCount(objectCount)}`}
        </h1>
        <p className="text-[12.5px] leading-relaxed text-[#6F7D7B] mb-6">
          {tx.railLede}
        </p>

        <ol className="space-y-0.5">
          {railLabels.map((label, i) => {
            const n = i + 1;
            const active = n === step;
            return (
              <li
                key={label}
                aria-current={active ? "step" : undefined}
                className={`flex items-start gap-2.5 rounded-[9px] px-2.5 py-2 ${
                  active
                    ? "bg-white shadow-[0_2px_8px_rgba(15,76,74,0.05)]"
                    : ""
                }`}
              >
                <span
                  className={`mt-0.5 w-[22px] h-[22px] shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                    active
                      ? "bg-[#0F4C4A] text-white"
                      : "bg-[#EAE4D8] text-[#7A8886]"
                  }`}
                >
                  {n}
                </span>
                <span className="min-w-0">
                  <span
                    className={`block text-[13px] font-semibold ${
                      active ? "text-[#0F4C4A]" : "text-[#3F4F4D]"
                    }`}
                  >
                    {label}
                  </span>
                  <span className="block text-[11.5px] text-[#7A8886] truncate">
                    {railDescs[i]}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden bg-white border-b border-[#ECE7DD]">
          <div className="px-5 h-[60px] flex items-center justify-between">
            <BrandWordmark className="h-5 w-auto" />
            <span className="text-xs font-semibold text-[#7A8886] tabular-nums">
              {step} / {total}
            </span>
          </div>
          <StageStrip step={step} labels={railLabels} />
        </header>

        <main className="flex-1 flex justify-center px-3 sm:px-6 py-6 sm:py-12">
          <div className="w-full max-w-[640px]">
            <div className="bg-white rounded-[20px] shadow-[0_20px_60px_rgba(15,76,74,0.10)] p-5 sm:px-10 sm:pt-9 sm:pb-8">
              {!hideChrome && <ProgressBar step={step} total={total} />}
              <div className={hideChrome ? undefined : "mt-5"}>{children}</div>
            </div>

            {onBack && !hideChrome && (
              <button
                onClick={onBack}
                className="mt-4 flex items-center gap-1.5 text-sm font-medium text-[#47605E] hover:text-[#0F4C4A] group touch-manipulation"
              >
                <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                <span>{tx.back}</span>
              </button>
            )}
          </div>
        </main>

        <footer className="py-5 text-center">
          <p className="text-xs text-[#7A8886] flex items-center justify-center gap-1.5">
            <Lock className="w-3 h-3" /> SSL-verschlüsselt · Coliviq GmbH ·
            info@coliviq.de
          </p>
        </footer>
      </div>
    </div>
  );
}

// ── Step 1: Welcome ────────────────────────────────────────────────────────

function Step1Welcome({
  name,
  onNext,
  tx,
}: {
  name: string;
  onNext: () => void;
  tx: typeof T.de | typeof T.en;
}) {
  const features = [
    { title: tx.featureVetted, sub: tx.featureVettedSub },
    { title: tx.featureRent, sub: tx.featureRentSub },
    { title: tx.featureTransparency, sub: tx.featureTransparencySub },
  ];

  return (
    <div>
      {/* The prototype opens on a plain card: eyebrow, headline, sub, then the
          promise rows. No hero panel — the step header already carries the
          weight, and a filled panel on top of a filled CTA competes with it. */}
      <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[#7A8886]">
        {tx.step} {1} {tx.of} 6
      </p>
      <h1 className="mt-1.5 text-[21px] sm:text-2xl font-bold text-[#0F4C4A] tracking-tight">
        {tx.welcomeHeadline}
      </h1>
      <p className="mt-1.5 text-sm text-[#6F7D7B] leading-relaxed">
        {tx.welcomeSub}
      </p>

      <p className="mt-5 text-[15px] leading-relaxed text-[#3F4F4D]">
        {tx.welcomeBody(name)}
      </p>

      <SectionLabel className="mt-6 mb-1">{tx.whatToExpect}</SectionLabel>

      <ul className="mb-7">
        {features.map((f, i) => (
          <li
            key={i}
            className="flex items-start gap-3 py-3.5 border-b border-[#ECE7DD] last:border-b-0"
          >
            <Check
              className="w-4 h-4 text-[#2FB3AA] shrink-0 mt-0.5"
              strokeWidth={2.5}
            />
            <div>
              <p className="text-sm font-semibold text-[#06201F]">{f.title}</p>
              <p className="text-[13px] text-[#6F7D7B] mt-0.5">{f.sub}</p>
            </div>
          </li>
        ))}
      </ul>

      {/* Trust row. Plain text in the prototype — the icons turned three short
          reassurances into a third badge strip on an already busy first screen. */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mb-7 text-xs text-[#7A8886]">
        {tx.trustSignals.map((signal) => (
          <span key={signal}>{signal}</span>
        ))}
      </div>

      <CardActions>
        <Button
          onClick={onNext}
          className="bg-[#0F4C4A] hover:bg-[#0B3A38] text-white h-11 px-6 text-sm font-semibold rounded-full shadow-[0_8px_24px_rgba(15,76,74,0.18)]"
        >
          {tx.getStarted}
        </Button>
      </CardActions>
    </div>
  );
}

// ── Step 2: Review Details ─────────────────────────────────────────────────

function Step2Details({
  form,
  email,
  property,
  objects,
  onSaveProperty,
  editingOwner,
  setEditingOwner,
  onChange,
  onNext,
  tx,
}: {
  form: FormState;
  email: string;
  property: PropertyData | null;
  /** Everything this invitation covers; more than one for a grouped invite. */
  objects: OnboardingObject[];
  /** Null while the invite covers more than one object: the correction is
      scoped to a single property, and the screen must not imply otherwise. */
  onSaveProperty:
    | ((patch: {
        title: string;
        street_name: string;
        street_number: string;
        zip_code: string;
        city: string;
      }) => Promise<null | CorrectionRefusal>)
    | null;
  editingOwner: boolean;
  setEditingOwner: (v: boolean) => void;
  onChange: (field: keyof FormState, value: string) => void;
  onNext: () => void;
  tx: typeof T.de | typeof T.en;
}) {
  // Only offered for a single-object invite: owner_update_onboarding_property
  // is scoped to one property, and a grouped invite would need a per-object
  // editor the mandate does not currently describe.
  const canEditProperty = Boolean(
    onSaveProperty && property && objects.length <= 1
  );
  // The summary rows describe ONE object. A grouped invite lists its objects
  // separately above, where each row already carries its own room count, so
  // there is nothing single to summarise here.
  const summaryObject = objects.length === 1 ? objects[0] : null;
  const [editingProperty, setEditingProperty] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  const [propError, setPropError] = useState<string | null>(null);
  const [propForm, setPropForm] = useState({
    title: "",
    street_name: "",
    street_number: "",
    zip_code: "",
    city: "",
  });
  const patchProp = (k: keyof typeof propForm, v: string) =>
    setPropForm((f) => ({ ...f, [k]: v }));

  const beginEditProperty = () => {
    if (!property) return;
    setPropForm({
      title: property.title ?? "",
      street_name: property.street_name ?? "",
      street_number: property.street_number ?? "",
      zip_code: property.zip_code ?? "",
      city: property.city ?? "",
    });
    setPropError(null);
    setEditingProperty(true);
  };

  const saveProperty = async () => {
    if (!onSaveProperty) return;
    setSavingProperty(true);
    setPropError(null);
    const reason = await onSaveProperty(propForm);
    setSavingProperty(false);
    if (reason === null) {
      setEditingProperty(false);
      return;
    }
    // The RPC's refusals are deterministic, so "try again" is wrong for two
    // of them: an owner of a shared building can retry forever and never
    // succeed, and an expired invite needs a reload, not a retry.
    setPropError(
      reason === "shared"
        ? tx.propertySaveShared
        : reason === "signed"
          ? tx.propertySaveSigned
          : reason === "invite"
            ? tx.propertySaveInvite
            : tx.propertySaveFailed
    );
  };

  return (
    <div>
      {/* Step header */}
      <div className="mb-5">
        <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[#7A8886] mb-1">
          Schritt 2
        </p>
        <h2 className="text-[21px] sm:text-2xl font-bold text-[#0F4C4A] tracking-tight mb-1.5">
          Ihre Daten prüfen
        </h2>
        <p className="text-sm text-[#6F7D7B]">{tx.step2Sub}</p>
      </div>

      {/* Owner card */}
      <div className="bg-white rounded-xl border border-[#E3DED3] mb-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#EFEBE3]">
          <div className="text-[11px] font-semibold text-[#7A8886] uppercase tracking-[0.08em]">
            {tx.ownerLabel}
          </div>
          <button
            onClick={() => setEditingOwner(!editingOwner)}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
              editingOwner
                ? "bg-[#0F4C4A] text-white hover:bg-[#0B3A38]"
                : "bg-[#F7F3EC] text-[#5B6B69] hover:bg-[#EFEBE3]"
            }`}
          >
            <Pencil className="w-3 h-3" />
            {editingOwner ? tx.doneLabel : tx.editLabel}
          </button>
        </div>
        <div className="p-5 space-y-3">
          {editingOwner ? (
            <>
              <div>
                <Label className="text-xs text-gray-500">{tx.fullName}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => onChange("name", e.target.value)}
                  className="mt-1"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">{tx.address}</Label>
                <Input
                  value={form.address}
                  onChange={(e) => onChange("address", e.target.value)}
                  className="mt-1"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">{tx.phone}</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => onChange("phone", e.target.value)}
                  className="mt-1"
                  autoComplete="off"
                />
              </div>
            </>
          ) : (
            <>
              <FieldBox label={tx.fullName} value={form.name || "—"} />
              <FieldBox label={tx.email} value={email || "—"} />
              <FieldBox label={tx.address} value={form.address || "—"} />
              <FieldBox label={tx.phone} value={form.phone || "—"} />
            </>
          )}
        </div>
      </div>

      {/* Property card. A grouped invite has no single `property` (its token
          carries property_id NULL), so the list alone is enough to show it. */}
      {(property || objects.length > 1) && (
        <div className="bg-white rounded-xl border border-[#E3DED3] mb-6 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[#EFEBE3]">
            <span className="text-[11px] font-semibold text-[#7A8886] uppercase tracking-[0.08em]">
              {objects.length > 1
                ? tx.propertiesLabel(objects.length)
                : tx.propertyLabel}
            </span>
            {canEditProperty && (
              <button
                type="button"
                onClick={() =>
                  editingProperty ? saveProperty() : beginEditProperty()
                }
                disabled={savingProperty}
                className="ml-auto text-xs font-semibold text-[#0F4C4A] hover:text-[#0B3A38] disabled:opacity-60"
              >
                {editingProperty ? tx.doneLabel : tx.editLabel}
              </button>
            )}
          </div>
          <div className="p-5 space-y-3">
            {/* A grouped invitation signs ONE mandate over every object, so
                the screen has to name them all. Showing the first and a
                total would let the owner accept a Vollmacht covering
                buildings they never saw. */}
            {objects.length > 1 ? (
              objects.map((obj) => (
                <DetailRow
                  key={obj.slot}
                  label={`${tx.propertyRowLabel} ${obj.slot}`}
                  value={obj.address || obj.title || "—"}
                />
              ))
            ) : editingProperty && property ? (
              <>
                <div>
                  <Label className="text-xs text-gray-500">
                    {tx.propertyTitleField}
                  </Label>
                  <Input
                    value={propForm.title}
                    onChange={(e) => patchProp("title", e.target.value)}
                    className="mt-1"
                    autoComplete="off"
                  />
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <div>
                    <Label className="text-xs text-gray-500">{tx.street}</Label>
                    <Input
                      value={propForm.street_name}
                      onChange={(e) => patchProp("street_name", e.target.value)}
                      className="mt-1"
                      autoComplete="off"
                    />
                  </div>
                  <div className="w-24">
                    <Label className="text-xs text-gray-500">
                      {tx.houseNumber}
                    </Label>
                    <Input
                      value={propForm.street_number}
                      onChange={(e) =>
                        patchProp("street_number", e.target.value)
                      }
                      className="mt-1"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-[7rem_1fr] gap-2">
                  <div>
                    <Label className="text-xs text-gray-500">
                      {tx.zipCode}
                    </Label>
                    <Input
                      value={propForm.zip_code}
                      onChange={(e) => patchProp("zip_code", e.target.value)}
                      className="mt-1"
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">
                      {tx.cityLabel}
                    </Label>
                    <Input
                      value={propForm.city}
                      onChange={(e) => patchProp("city", e.target.value)}
                      className="mt-1"
                      autoComplete="off"
                    />
                  </div>
                </div>
                {propError && (
                  <p className="text-xs text-[#B4504B]" role="alert">
                    {propError}
                  </p>
                )}
              </>
            ) : (
              <>
                {/* The approved mockup states the object as bordered review
                    fields, matching the owner block above: address full width,
                    then Wohnflaeche / Zimmer / Status across one row. */}
                <FieldBox
                  label={tx.address}
                  value={
                    [property?.address, property?.zip_code, property?.city]
                      .filter(Boolean)
                      .join(", ") ||
                    (property?.title ?? "—")
                  }
                />
                {summaryObject && (
                  <div className="grid grid-cols-3 gap-3">
                    <FieldBox
                      label={tx.livingArea}
                      value={
                        summaryObject.livingArea != null
                          ? `${summaryObject.livingArea} m²`
                          : "—"
                      }
                    />
                    <FieldBox
                      label={tx.rooms}
                      value={
                        summaryObject.rooms != null && summaryObject.rooms > 0
                          ? String(summaryObject.rooms)
                          : "—"
                      }
                    />
                    {/* displayStatus, not tenancyStatus: the latter decides
                        whether the object bills, and grouped invites leave it
                        unset on purpose. Showing the real status must not move
                        a price. An object with no status recorded renders a
                        dash rather than guessing "Vermietet". */}
                    <FieldBox
                      label={tx.rentalStatus}
                      value={
                        summaryObject.displayStatus
                          ? isVacantStatus(summaryObject.displayStatus)
                            ? tx.statusVacant
                            : tx.statusRented
                          : "—"
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {editingOwner && (
        <div className="mb-3 flex items-start gap-2 rounded-lg bg-[#FDF6E7] border border-[#EFE3C8] px-3 py-2 text-sm text-[#8A6B54]">
          <span className="leading-5">⚠️</span>
          <span>
            {tx.saveBeforeContinue}
          </span>
        </div>
      )}
      <Button
        onClick={onNext}
        disabled={!form.name || editingOwner}
        className="bg-[#0F4C4A] hover:bg-[#0B3A38] text-white h-11 px-6 text-sm font-semibold rounded-full shadow-[0_8px_24px_rgba(15,76,74,0.18)] disabled:bg-[#E6E1D8] disabled:text-[#9AA3A1] disabled:opacity-100 disabled:cursor-not-allowed disabled:shadow-none"
      >
        {tx.confirmContinue} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

/**
 * A read-only value in a bordered box, as the approved mockup shows the
 * review fields -- distinct from DetailRow, which is a label/value line with
 * no box and is still what the property card uses.
 */
function FieldBox({
  label,
  value,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[12.5px] text-[#5B6B69] mb-1.5">{label}</div>
      <div className="border border-[#E3DED3] rounded-lg py-2.5 px-3.5 text-sm text-[#06201F] bg-white">
        {value}
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="flex items-baseline gap-3 flex-wrap sm:flex-nowrap">
      <span className="text-sm text-[#5B6B69] w-full sm:min-w-[110px] sm:w-auto">
        {label}
      </span>
      <span className="text-sm font-semibold text-[#06201F]">{value || "—"}</span>
    </div>
  );
}

// ── Step 3: Services ───────────────────────────────────────────────────────

function Step3Services({
  feeAmount,
  numRooms,
  objects,
  docs,
  onNext,
  tx,
}: {
  feeAmount: number | null;
  numRooms: number;
  objects: OnboardingObject[];
  docs: PlatformDoc[];
  onNext: () => void;
  tx: typeof T.de | typeof T.en;
}) {
  // The rate the ORDER is priced on, not the one snapshotted on the token.
  // Each object carries its own fee, so a grouped invite whose objects were
  // repriced would otherwise headline a number the summary never charges.
  // uniformFeePerRoom returns null when the objects disagree; there is no
  // single rate to headline then, so the per-object rows below carry it.
  // `?? feeAmount` would defeat the point: uniformFeePerRoom returns null
  // precisely BECAUSE the objects disagree, and falling back to the token's
  // rate would headline a single number the order never charges. The token
  // fee is the fallback only when there are no objects to read at all.
  const rate =
    objects.length > 0 ? uniformFeePerRoom(objects) : feeAmount;
  const agbDoc = docs.find((d) => d.document_key === "agb");

  return (
    <div>
      <div className="mb-5">
        <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[#7A8886] mb-1">
          Schritt 3
        </p>
        <h2 className="text-[21px] sm:text-2xl font-bold text-[#0F4C4A] tracking-tight mb-1.5">
          {tx.step3Headline}
        </h2>
        <p className="text-sm text-[#6F7D7B]">
          {tx.lang === "de" ? (
            <>
              Full-Service-Verwaltung gemäß § 3 der{" "}
              {agbDoc?.file_url ? (
                <a
                  href={agbDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0F4C4A] underline underline-offset-2 hover:text-[#0B3A38]"
                >
                  AGBs
                </a>
              ) : (
                "AGBs"
              )}
              .
            </>
          ) : (
            tx.step3Sub
          )}
        </p>
      </div>

      {/* Fee. The prototype sets the rate as a large petrol figure on the
          card itself rather than a filled panel — this screen already ends in
          a filled CTA, and two filled surfaces competed for the same read. */}
      {rate && (
        <div className="mt-5">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[40px] leading-none font-semibold text-[#0F4C4A] tracking-[-0.03em]">
              {fmtEuro(rate, tx, { exact: true })}
            </span>
            <span className="text-[13.5px] text-[#6F7D7B]">
              {tx.perRoomMonth}
            </span>
          </div>
          {/* The rate is net — the first number the owner reads must say so,
              and show what it comes to with VAT. */}
          <div className="mt-1.5 text-[12.5px] text-[#7A8886]">
            {tx.vatNote(fmtEuro(orderFeeTotals(rate).totalGross, tx))}
          </div>
          {/* Two pills, stacked: what the owner does NOT pay. The
              re-letting one is Neel's own copy (COL-564) — if the AGB does
              not in fact waive the fee the wording changes, the pill does
              not quietly disappear. */}
          <div className="mt-3 flex flex-col items-start gap-2">
            <span className="inline-block rounded-full bg-[#2FB3AA]/[0.16] px-3 py-1.5 text-xs font-semibold text-[#0F4C4A]">
              {tx.noVacancyFee}
            </span>
            <span className="inline-block rounded-full bg-[#2FB3AA]/[0.16] px-3 py-1.5 text-xs font-semibold text-[#0F4C4A]">
              {tx.noRelettingFee}
            </span>
          </div>
        </div>
      )}

      {/* Scope of services. The prototype lists these as a turquoise check and
          two lines on a hairline rule — one card per service put eight filled
          boxes on a screen whose job is to be read, and the emoji carried no
          meaning the title did not already. */}
      <SectionLabel className="mt-6 mb-1">{tx.servicesLabel}</SectionLabel>
      <ul className="mb-7">
        {tx.services.map((s, i) => (
          <li
            key={i}
            className="flex items-start gap-3 py-3.5 border-b border-[#ECE7DD]"
          >
            <Check
              className="w-4 h-4 text-[#2FB3AA] shrink-0 mt-0.5"
              strokeWidth={2.5}
            />
            <div>
              <p className="text-sm font-semibold text-[#06201F]">{s.title}</p>
              <p className="text-[13px] text-[#6F7D7B] mt-0.5 leading-relaxed">
                {s.sub}
              </p>
            </div>
          </li>
        ))}

        {/* Platform access is the last line of the same list in the prototype,
            not a filled panel hanging off the end of it. */}
        <li className="flex items-start gap-3 py-3.5">
          <Check
            className="w-4 h-4 text-[#2FB3AA] shrink-0 mt-0.5"
            strokeWidth={2.5}
          />
          <div>
            <p className="text-sm font-semibold text-[#06201F]">
              {tx.platformTitle}
            </p>
            <p className="text-[13px] text-[#6F7D7B] mt-0.5 leading-relaxed">
              {tx.platformSub}
            </p>
          </div>
        </li>
      </ul>

      <CardActions>
        <Button
          onClick={onNext}
          className="bg-[#0F4C4A] hover:bg-[#0B3A38] text-white h-11 px-6 text-sm font-semibold rounded-full shadow-[0_8px_24px_rgba(15,76,74,0.18)]"
        >
          {tx.continueToOrder}
        </Button>
      </CardActions>
    </div>
  );
}

function DocumentStepHeader({
  step,
  total,
  title,
  subtitle,
  onClose,
  tx,
}: {
  step: number;
  total: number;
  title: string;
  subtitle: string;
  onClose?: () => void;
  tx: typeof T.de | typeof T.en;
}) {
  const [confirmClose, setConfirmClose] = useState(false);
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-semibold tracking-[0.1em] uppercase text-[#5F7C7A]">
          {tx.step} {step} {tx.of} {total}
        </p>
        {onClose && (
          <button
            type="button"
            onClick={() => setConfirmClose(true)}
            aria-label={tx.closeLabel}
            className="-mt-1.5 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#F3EFE7] text-sm text-[#5B6B69] hover:bg-[#EAE4D8]"
          >
            <span aria-hidden>×</span>
          </button>
        )}
      </div>
      <h2 className="mt-2 text-[22px] font-semibold tracking-[-0.01em] text-[#0F4C4A]">
        {title}
      </h2>
      <p className="mt-1 text-[13.5px] text-[#6F7D7B]">{subtitle}</p>
      <div className="mt-[18px] mb-[26px]">
        <ProgressBar step={step} total={total} />
      </div>

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tx.leaveTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {tx.leaveDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tx.leaveCancel}</AlertDialogCancel>
            <AlertDialogAction onClick={onClose}>
              {tx.leaveConfirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Step 4: Contract & Payment ─────────────────────────────────────────────

export function Step4Contract({
  form,
  property,
  feeAmount,
  numRooms,
  objects,
  docs,
  onChange,
  onSubmit,
  onBack,
  onClose,
  step,
  total,
  submitting,
  tx,
}: {
  form: FormState;
  property: PropertyData | null;
  feeAmount: number | null;
  numRooms: number;
  /** Everything this invitation covers; the binding total is its sum. */
  objects: OnboardingObject[];
  docs: PlatformDoc[];
  onChange: (field: keyof FormState, value: string | boolean) => void;
  onSubmit: () => void;
  onBack: () => void;
  onClose: () => void;
  step: number;
  total: number;
  submitting: boolean;
  tx: typeof T.de | typeof T.en;
}) {
  const [showDocModal, setShowDocModal] = useState(false);
  const [bankTouched, setBankTouched] = useState({
    holder: false,
    iban: false,
  });
  const verwDoc = docs.find((d) => d.document_key === "verwaltervollmacht");
  const agbDoc = docs.find((d) => d.document_key === "agb");
  // ONE net figure for the binding order. A vacant property bills nothing
  // (Neel, 2026-10-04) — the fee comes out of rent we collect, and there is no
  // rent while nobody lives there. The same rule runs server-side when the
  // Auftragsbestätigung is generated, so the document matches this screen.
  // One breakdown, shared with both Auftragsbestätigung generators, so the
  // total the owner accepts here and the totals printed on the documents are
  // the same already-rounded numbers rather than three separate calculations.
  // Vacancy basis: null, as it has effectively been. This screen never had a
  // tenancy status to read -- the column does not exist on `properties` -- and
  // both Auftragsbestaetigung generators hardcode null for the same reason
  // (see send-owner-contracts: "Both surfaces bill, consistently"). Billing is
  // therefore unchanged by this fix; what changes is that the Objekt card can
  // finally show the address.
  // The binding order is priced from an OBJECT LIST, the same module and the
  // same inputs the two Auftragsbestaetigung generators use, so the total the
  // owner accepts here is the total both documents print. A single-property
  // invite is a list of one; grouped invites arrive with more.
  //
  // tenancyStatus null, as it has always effectively been on this line: there
  // is no tenancy_status column on `properties` to read (see the property
  // select above), and both generators hardcode null for the same reason.
  const orderFee = orderFeeTotals(totalNetFee(objects));
  const orderNetTotal = orderFee.totalNet;
  const vacant = objects.length > 0 && objects.every(isVacant);
  // sign-vollmacht and send-owner-contracts refuse (422) an order whose room
  // count cannot be priced. Without the same guard here the owner is shown a
  // binding total of 0,00 EUR, accepts it, and only then hits the refusal --
  // a price of zero on an order screen is worse than no price at all.
  const priceable = isOrderPriceable(objects);

  // BOTH ticks are required now that they are separate consents: one is the
  // statutory Widerrufsbelehrung attaching to a chargeable order, the other
  // accepts the AGB and the Verwaltervollmacht.
  const ibanOk = isValidIban(form.iban);
  const canSubmit =
    priceable &&
    form.withdrawal_policy_accepted &&
    form.management_agreement_accepted &&
    // Require the account holder and a valid IBAN before placing the order.
    form.bank_holder &&
    ibanOk &&
    !submitting;

  const contractStart = new Date();
  contractStart.setDate(1);
  contractStart.setMonth(contractStart.getMonth() + 1);
  const contractStartStr = contractStart.toLocaleDateString(tx.dateLocale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div>
      <DocumentStepHeader
        step={step}
        total={total}
        title={tx.step4Headline}
        subtitle={tx.step4Sub}
        onClose={onClose}
        tx={tx}
      />
      <SectionLabel className="mb-3">{tx.bankSectionLabel}</SectionLabel>
      <p className="text-[12.5px] leading-relaxed text-[#6F7D7B] -mt-1.5 mb-3.5">
        {tx.bankSectionNote}
      </p>
      <div className="flex flex-col gap-3.5">
        <div>
          <Label
            htmlFor="owner-bank-holder"
            className="block mb-1.5 text-[12.5px] font-normal text-[#5B6B69]"
          >
            {tx.accountHolder} *
          </Label>
          <Input
            id="owner-bank-holder"
            value={form.bank_holder}
            onChange={(e) => onChange("bank_holder", e.target.value)}
            onBlur={() => setBankTouched((t) => ({ ...t, holder: true }))}
            aria-invalid={bankTouched.holder && !form.bank_holder}
            aria-describedby={
              bankTouched.holder && !form.bank_holder
                ? "owner-bank-holder-error"
                : undefined
            }
            className="h-auto rounded-lg border-[#E3DED3] bg-white px-3.5 py-[11px] text-sm text-[#06201F]"
            placeholder={tx.namePlaceholder}
            autoComplete="name"
          />
          {bankTouched.holder && !form.bank_holder && (
            <p
              id="owner-bank-holder-error"
              className="text-xs text-red-400 mt-1"
            >
              {tx.required}
            </p>
          )}
        </div>
        <div>
          <Label
            htmlFor="owner-bank-iban"
            className="block mb-1.5 text-[12.5px] font-normal text-[#5B6B69]"
          >
            IBAN *
          </Label>
          <Input
            id="owner-bank-iban"
            value={form.iban}
            onChange={(e) => {
              onChange("iban", formatIban(e.target.value));
              // The hidden BIC belongs to the prefilled account. An edited
              // IBAN must never submit that previous bank's BIC.
              onChange("bic", "");
            }}
            onBlur={() => setBankTouched((t) => ({ ...t, iban: true }))}
            aria-invalid={bankTouched.iban && !ibanOk}
            aria-describedby={
              bankTouched.iban && !ibanOk ? "owner-bank-iban-error" : undefined
            }
            className="h-auto rounded-lg border-[#E3DED3] bg-white px-3.5 py-[11px] font-mono text-[13px] text-[#06201F]"
            placeholder="DE00 0000 0000 0000 0000 00"
            autoComplete="off"
          />
          {bankTouched.iban && !ibanOk && (
            <p id="owner-bank-iban-error" className="text-xs text-red-400 mt-1">
              {form.iban.trim() ? tx.ibanInvalid : tx.required}
            </p>
          )}
        </div>
      </div>

      {/* Order summary. The prototype states the order as plain label/value
          rows under one eyebrow rather than a filled panel: this is the figure
          the owner is about to be bound to, and the panel styled it like a
          promotion. */}
      {(property || feeAmount || objects.length > 0) && (
        <>
          <SectionLabel className="mt-[26px] mb-3">
            {tx.yourOrder}
            {objects.length > 1 && ` · ${tx.propertyCount(objects.length)}`}
          </SectionLabel>
          <div>
            <SummaryRow label={tx.contractStart} value={contractStartStr} />
            {objects.length > 1
              ? objects.map((obj) => (
                  <SummaryRow
                    key={obj.slot}
                    label={`${tx.propertyOrderLabel} ${obj.slot}`}
                    value={obj.address || obj.title || "—"}
                  />
                ))
              : property && (
                  <SummaryRow
                    label={tx.propertyOrderLabel}
                    value={
                      [property.address, property.zip_code, property.city]
                        .filter(Boolean)
                        .join(", ") ||
                      property.title ||
                      "—"
                    }
                  />
                )}
            {!priceable && (
              <p
                role="alert"
                className="text-[13px] leading-relaxed text-[#B4504B] bg-[#FDF2F1] border border-[#F3D5D2] rounded-xl px-4 py-3"
              >
                {tx.feeNotPriceable}
              </p>
            )}
            {priceable && feeAmount != null && (
              <SummaryRow
                label={tx.managementFeeOrder}
                value={
                  vacant ? (
                    <span>
                      {fmtEuro(0, tx, { exact: true })}{" "}
                      <span className="text-[#6F7D7B]">· {tx.vacantNoFee}</span>
                    </span>
                  ) : (
                    `${fmtEuro(orderNetTotal, tx)} ${tx.netLabel}`
                  )
                }
              />
            )}

            {/* Total: the last row of the section, so it drops its rule and
                carries the VAT split underneath rather than in a row of its own. */}
            {priceable && feeAmount != null && (
              <>
                <div className="flex justify-between items-baseline gap-4 pt-3.5 text-[13.5px] font-semibold text-[#06201F]">
                  <span>{tx.monthlyTotal}</span>
                  <span className="tabular-nums">
                    {fmtEuro(orderFee.totalGross, tx, { exact: vacant })}
                  </span>
                </div>
                <div className="flex justify-between items-baseline gap-4 mt-1 text-xs text-[#6F7D7B]">
                  <span>{tx.vatShare}</span>
                  <span className="tabular-nums">
                    {fmtEuro(orderFee.vat, tx, { exact: vacant })}
                  </span>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Two separate consents, both mandatory. These used to be one tick that
          set the withdrawal notice, the AGB and the order together — a single
          tick cannot evidence that the owner was shown the Widerrufsbelehrung
          as its own act, and the prototype asks for them separately. */}
      <div className="flex flex-col gap-3 mt-[22px]">
        <ConsentRow
          checked={form.withdrawal_policy_accepted}
          onChange={(next) => onChange("withdrawal_policy_accepted", next)}
        >
          Ich habe die{" "}
          {agbDoc?.file_url ? (
            <a
              href={`${agbDoc.file_url}#page=${AGB_WITHDRAWAL_PAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2FB3AA] underline underline-offset-2 hover:text-[#0F4C4A]"
            >
              Widerrufsbelehrung
            </a>
          ) : (
            <span>Widerrufsbelehrung</span>
          )}{" "}
          gelesen und akzeptiere den verbindlichen, kostenpflichtigen Auftrag.
        </ConsentRow>
        <ConsentRow
          checked={form.management_agreement_accepted}
          onChange={(next) => {
            onChange("management_agreement_accepted", next);
            onChange("agb_accepted", next);
          }}
        >
          Ich habe die{" "}
          {agbDoc?.file_url ? (
            <a
              href={agbDoc.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2FB3AA] underline underline-offset-2 hover:text-[#0F4C4A]"
            >
              AGBs
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setShowDocModal(true)}
              className="text-[#2FB3AA] underline underline-offset-2 hover:text-[#0F4C4A]"
            >
              AGBs
            </button>
          )}{" "}
          gelesen und akzeptiere diese.
        </ConsentRow>
      </div>

      <CardActions onBack={onBack} backLabel={tx.back}>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          className="bg-[#0F4C4A] hover:bg-[#0B3A38] text-white h-11 px-6 text-sm font-semibold rounded-full disabled:bg-[#E6E1D8] disabled:text-[#9AA3A1] disabled:opacity-100 disabled:cursor-not-allowed disabled:shadow-none"
        >
          {submitting ? tx.processing : tx.submitBtn}
        </Button>
      </CardActions>

      {/* Document download modal */}
      {showDocModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-4 pb-4 sm:pb-0"
          onClick={() => setShowDocModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-bold text-gray-900 mb-4">
              {tx.docModalTitle}
            </h3>
            <div className="space-y-3">
              {[
                { doc: verwDoc, label: tx.docVerwaltervollmacht },
                { doc: agbDoc, label: tx.docAgb },
              ].map(({ doc, label }) => (
                <div
                  key={label}
                  className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl p-3.5 border border-gray-100"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <FileText className="w-4 h-4 text-[#2FB3AA] shrink-0" />
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {label}
                    </span>
                  </div>
                  {doc?.file_url ? (
                    <a
                      href={doc.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-xs font-semibold text-[#0F4C4A] bg-[#E3EFED] hover:bg-[#CFE4E2] px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <Download className="w-3.5 h-3.5" />
                      {tx.docDownload}
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400 shrink-0">
                      {tx.docNotAvailable}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowDocModal(false)}
              className="mt-5 w-full text-sm text-gray-400 hover:text-gray-600 font-medium"
            >
              {tx.closeLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrderRow({
  label,
  value,
  sub,
  bold,
}: {
  label: string;
  value: string;
  sub?: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span
        className={`text-sm ${bold ? "font-semibold text-gray-900" : "text-gray-500"}`}
      >
        {label}
      </span>
      <div className="text-right">
        <span
          className={`text-sm ${bold ? "font-bold text-gray-900" : "text-gray-900"}`}
        >
          {value}
        </span>
        {sub && <div className="text-xs text-gray-400">{sub}</div>}
      </div>
    </div>
  );
}

// ── Step 5: Power of Attorney ─────────────────────────────────────────────

type TemplatePreview = {
  id: string;
  name: string;
  description: string | null;
  pdf_file_name: string | null;
  preview_url: string | null;
};

type SignatureDraft = {
  strokes: ReturnType<SignaturePad["toData"]>;
  width: number;
};

export function Step5Vollmacht({
  form,
  property,
  objects,
  docs = [],
  submissionId,
  onboardingToken,
  ownerSignatureData,
  signatureDraftRef,
  step,
  total,
  sigPadRef,
  onChange,
  onSubmit,
  onBack,
  onClose,
  submitting,
  tx,
}: {
  form: FormState;
  property: PropertyData | null;
  /** Everything the mandate covers; one Vollmacht is signed over all of it. */
  objects: OnboardingObject[];
  docs?: PlatformDoc[];
  submissionId: string | null;
  /** The invite-link token; the onboarding edge functions require it (COL-492). */
  onboardingToken: string | null;
  ownerSignatureData: string | null;
  signatureDraftRef: React.MutableRefObject<SignatureDraft | null>;
  step: number;
  total: number;
  sigPadRef: React.RefObject<SignaturePad>;
  onChange: (field: keyof FormState, value: string | boolean) => void;
  onSubmit: () => void;
  onBack?: () => void;
  onClose?: () => void;
  submitting: boolean;
  tx: typeof T.de | typeof T.en;
}) {
  const [templates, setTemplates] = React.useState<TemplatePreview[]>([]);
  const [loadingPreviews, setLoadingPreviews] = React.useState(true);
  // The preview refuses a grouped invite whose templates do not place every
  // numbered property slot (TEMPLATE_SLOTS_MISSING) — the same refusal
  // sign-vollmacht makes at submit. Surfaced as a blocking error HERE, where
  // the owner has not yet drawn a signature; swallowed like other preview
  // failures, they would sign into a generic toast at the very end.
  const [templateIncomplete, setTemplateIncomplete] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);
  const [hasSignature, setHasSignature] = React.useState(false);

  // Keep the canvas bitmap aligned with its CSS size, including mobile and
  // browser zoom. Preserve drawn strokes when the available width changes.
  React.useLayoutEffect(() => {
    const pad = sigPadRef.current;
    if (!pad) return;
    const canvas = pad.getCanvas();
    let previousWidth =
      signatureDraftRef.current?.width || canvas.getBoundingClientRect().width;
    if (signatureDraftRef.current) {
      pad.fromData(signatureDraftRef.current.strokes);
      setHasSignature(!pad.isEmpty());
    }
    const resize = () => {
      const width = canvas.getBoundingClientRect().width;
      if (!width) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const pixelWidth = Math.round(width * ratio);
      const pixelHeight = Math.round(96 * ratio);
      if (
        canvas.width === pixelWidth &&
        canvas.height === pixelHeight &&
        previousWidth === width
      )
        return;
      const strokes = pad.toData();
      const scale = previousWidth ? width / previousWidth : 1;
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
      canvas.getContext("2d")?.scale(ratio, ratio);
      pad.clear();
      strokes.forEach((stroke) => {
        stroke.forEach((point) => {
          point.x *= scale;
        });
      });
      pad.fromData(strokes);
      previousWidth = width;
      signatureDraftRef.current = { strokes: pad.toData(), width };
      setHasSignature(!pad.isEmpty());
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    window.addEventListener("resize", resize);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [sigPadRef, signatureDraftRef]);

  const propertyAddress =
    [property?.address, property?.zip_code, property?.city]
      .filter(Boolean)
      .join(", ") ||
    property?.title ||
    "";
  React.useEffect(() => {
    if (!submissionId || !onboardingToken) {
      setLoadingPreviews(false);
      return;
    }
    supabase.functions
      .invoke("preview-owner-contracts", {
        body: {
          submission_id: submissionId,
          token: onboardingToken,
          owner_signature_data: ownerSignatureData ?? undefined,
        },
      })
      .then(async ({ data, error }) => {
        // supabase-js buries the JSON body of a non-2xx response in
        // error.context; error.message is a generic one-liner. Only the
        // slot-coverage refusal blocks — any other failure keeps today's
        // silent fallback to the built-in summary.
        if (error && "context" in error) {
          const body = await (error.context as Response)
            .json()
            .catch(() => null);
          if (body?.code === "TEMPLATE_SLOTS_MISSING") {
            setTemplateIncomplete(true);
          }
        }
        if (data?.templates?.length) {
          setTemplates(data.templates);
        }
        setLoadingPreviews(false);
      })
      .catch(() => setLoadingPreviews(false));
  }, [submissionId, onboardingToken, ownerSignatureData]);

  const hasTemplates = templates.length > 0;
  const activeTpl = templates[activeTab] ?? null;
  const vollmachtUrl =
    docs.find((doc) => doc.document_key === "verwaltervollmacht")?.file_url;

  return (
    <div>
      <DocumentStepHeader
        step={step}
        total={total}
        title={tx.step5Headline}
        subtitle={tx.step5Sub}
        onClose={onClose}
        tx={tx}
      />

      {loadingPreviews ? (
        /* Loading skeleton */
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 mb-4 flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#0F4C4A] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-400">{tx.step5LoadingPreview}</p>
        </div>
      ) : templateIncomplete ? (
        /* Blocking: the mandate cannot name every Liegenschaft of this
           invite, so there is nothing valid to preview or sign. */
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 leading-relaxed">
            {tx.step5TemplateIncomplete}
          </p>
        </div>
      ) : hasTemplates ? (
        /* Multi-template view */
        <div className="mb-4">
          {/* Tab strip — only show when >1 template */}
          {templates.length > 1 && (
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
              {templates.map((tpl, i) => (
                <button
                  key={tpl.id}
                  onClick={() => setActiveTab(i)}
                  className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                    activeTab === i
                      ? "bg-[#0F4C4A] text-white border-[#0F4C4A] shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-[#9FD3CE]"
                  }`}
                >
                  <FileText className="w-3 h-3 shrink-0" />
                  <span className="truncate max-w-[120px]">{tpl.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Active template card */}
          {activeTpl && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Card header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#0F4C4A] to-[#0B3A38]">
                <div>
                  <p className="text-xs font-bold text-[#CFE4E2] uppercase tracking-widest mb-0.5">
                    {templates.length > 1
                      ? tx.step5DocOf(activeTab + 1, templates.length)
                      : "Coliviq GmbH"}
                  </p>
                  <p className="text-sm font-bold text-white">
                    {activeTpl.name}
                  </p>
                  {activeTpl.description && (
                    <p className="text-xs text-[#CFE4E2] mt-0.5">
                      {activeTpl.description}
                    </p>
                  )}
                </div>
                <span className="text-xs text-[#CFE4E2] bg-white/20 px-2 py-0.5 rounded-full shrink-0 ml-2">
                  PDF
                </span>
              </div>

              {/* PDF preview area */}
              {activeTpl.preview_url ? (
                (() => {
                  const isMobile = /iPhone|iPad|iPod|Android/i.test(
                    navigator.userAgent
                  );
                  return isMobile ? (
                    /* Mobile: can't embed PDFs — show a tap-to-open card */
                    <div className="flex flex-col items-center justify-center gap-4 p-6 bg-gray-50 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-[#E3EFED] border border-[#CFE4E2] flex items-center justify-center">
                        <FileText className="w-8 h-8 text-[#2FB3AA]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800 mb-1">
                          {activeTpl.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {tx.step5PreviewNote}
                        </p>
                      </div>
                      <a
                        href={activeTpl.preview_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-[#0F4C4A] hover:bg-[#0B3A38] active:bg-[#0B3A38] text-white text-sm font-semibold px-5 py-3 rounded-xl transition-colors shadow-sm shadow-[0_8px_24px_rgba(15,76,74,0.18)] touch-manipulation"
                      >
                        <FileText className="w-4 h-4" />
                        {tx.step5OpenInTab}
                      </a>
                    </div>
                  ) : (
                    /* Desktop: embed PDF directly */
                    <div
                      className="relative bg-gray-50"
                      style={{ height: "420px" }}
                    >
                      <object
                        data={activeTpl.preview_url}
                        type="application/pdf"
                        className="w-full h-full"
                      >
                        <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
                          <FileText className="w-10 h-10 text-gray-300" />
                          <p className="text-sm text-[#6F7D7B] font-medium">
                            {activeTpl.name}
                          </p>
                          <a
                            href={activeTpl.preview_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-[#0F4C4A] bg-[#E3EFED] hover:bg-[#CFE4E2] px-3 py-1.5 rounded-lg transition-colors"
                          >
                            {tx.step5OpenInTab}
                          </a>
                        </div>
                      </object>
                      <a
                        href={activeTpl.preview_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-2 right-2 text-xs font-medium text-[#0B3A38] bg-white/90 border border-[#CFE4E2] hover:bg-[#E3EFED] px-2.5 py-1 rounded-lg shadow-sm transition-colors"
                      >
                        {tx.step5OpenInTab}
                      </a>
                    </div>
                  );
                })()
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 text-center p-6 bg-gray-50">
                  <FileText className="w-8 h-8 text-gray-300" />
                  <p className="text-sm text-gray-400">{tx.step5NoPreview}</p>
                </div>
              )}

              {/* Pre-fill summary */}
              <div className="px-4 py-3 border-t border-gray-100 bg-[#E3EFED]/40 text-xs text-[#0B3A38] flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#2FB3AA] shrink-0" />
                {tx.step5PreviewNote}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Built-in summary follows the same fields and rows as the reference.
           Keep every authority visible when no generated PDF is available. */
        <section aria-label={tx.step5Headline}>
          <p className="mb-3.5 text-xs text-[#6F7D7B]">{tx.step5DocFilename}</p>
          <div className="flex flex-col gap-3.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <p className="mb-1.5 text-[12.5px] text-[#5B6B69]">
                  {tx.step5Principal}
                </p>
                <div className="rounded-lg border border-[#E3DED3] px-3.5 py-[11px] text-sm text-[#06201F] break-words">
                  {form.name || "—"}
                </div>
              </div>
              <div>
                <p className="mb-1.5 text-[12.5px] text-[#5B6B69]">
                  {tx.step5Agent}
                </p>
                <div className="rounded-lg border border-[#E3DED3] px-3.5 py-[11px] text-sm text-[#06201F]">
                  Coliviq GmbH
                </div>
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-[12.5px] text-[#5B6B69]">
                {objects.length > 1 ? tx.step5Properties : tx.step5Property}
              </p>
              <div className="rounded-lg border border-[#E3DED3] px-3.5 py-[11px] text-sm text-[#06201F] break-words">
                {objects.length > 1 ? (
                  objects.map((obj) => (
                    <p key={obj.slot}>{obj.address || obj.title || "—"}</p>
                  ))
                ) : (
                  <p>
                    {propertyAddress ||
                      objects[0]?.address ||
                      objects[0]?.title ||
                      "—"}
                  </p>
                )}
              </div>
            </div>
          </div>
          <SectionLabel className="mt-[26px] mb-3">
            {tx.step5AuthTitle}
          </SectionLabel>
          <ol className="text-[13px] text-[#3F4F4D]">
            {tx.step5AuthLines.slice(1).map((line, i) => (
              <li
                key={line}
                className="flex gap-3 border-b border-[#EFEBE3] py-[9px]"
              >
                <span aria-hidden className="w-[18px] shrink-0 text-[#6F7D7B]">
                  {i + 1}.
                </span>
                <span>{line.replace(/^\d+\.\s*/, "")}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Signature. Captured here and applied to every document of this
          onboarding — but held in the browser until the binding order in
          step 5, so nothing is transmitted before the Widerrufsbelehrung. */}
      <SectionLabel className="mt-[26px] mb-3">
        {tx.step5SignatureLabel}
      </SectionLabel>
      <p className="text-[12.5px] text-[#6F7D7B] -mt-1.5 mb-2.5">
        {tx.step5SignatureNote}
      </p>
      <div className="relative rounded-[10px] border border-dashed border-[#D5DDDC] bg-[#FAF8F4] overflow-hidden">
        {!hasSignature && (
          <span className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-[12.5px] italic text-[#A3ADAB]">
            {tx.signaturePlaceholder}
          </span>
        )}
        <SignaturePad
          ref={sigPadRef}
          onEnd={() => {
            const pad = sigPadRef.current;
            if (!pad) return;
            signatureDraftRef.current = {
              strokes: pad.toData(),
              width: pad.getCanvas().getBoundingClientRect().width,
            };
            setHasSignature(!pad.isEmpty());
          }}
          clearOnResize={false}
          canvasProps={{
            className: "block h-24 w-full touch-none",
            "aria-label": tx.step5SignatureLabel,
          }}
          // Transparent so the exported PNG has no background — otherwise the
          // signature renders inside a grey box in the contract PDF.
          backgroundColor="rgba(0,0,0,0)"
        />
      </div>
      <div className="flex justify-end mt-2">
        <button
          type="button"
          onClick={() => {
            sigPadRef.current?.clear();
            signatureDraftRef.current = null;
            setHasSignature(false);
          }}
          className="text-xs text-[#2FB3AA] hover:text-[#0F4C4A] underline underline-offset-2"
        >
          {tx.step5ClearSignature}
        </button>
      </div>

      {/* Granting the Vollmacht is its own act, separate from drawing a
          signature: without it an owner could sign the power of attorney
          without ever confirming they grant it. */}
      <div className="mt-[18px]">
        <ConsentRow
          checked={form.docs_accepted}
          onChange={(next) => onChange("docs_accepted", next)}
        >
          {tx.vollmachtConsentPre}
          {vollmachtUrl ? (
            <a
              href={vollmachtUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2FB3AA] underline underline-offset-2 hover:text-[#0F4C4A]"
            >
              {tx.vollmachtConsentLink}
            </a>
          ) : (
            <span>{tx.vollmachtConsentLink}</span>
          )}
          {tx.vollmachtConsentPost}
        </ConsentRow>
      </div>

      <CardActions onBack={onBack} backLabel={tx.back}>
        <Button
          onClick={onSubmit}
          disabled={
            submitting ||
            !hasSignature ||
            !form.docs_accepted ||
            loadingPreviews ||
            templateIncomplete
          }
          className="bg-[#0F4C4A] hover:bg-[#0B3A38] disabled:bg-[#E6E1D8] disabled:text-[#9AA3A1] disabled:opacity-100 disabled:cursor-not-allowed text-white h-11 px-6 text-sm font-semibold rounded-full shadow-[0_8px_24px_rgba(15,76,74,0.18)] disabled:shadow-none"
        >
          {submitting ? tx.step5Submitting : tx.step5Submit}
        </Button>
      </CardActions>
    </div>
  );
}

// ── Step 6: Thank you ──────────────────────────────────────────────────────

function Step6ThankYou({
  name,
  reference,
  objects,
  signedPdfDocs,
  onGoToDashboard,
  tx,
}: {
  name: string;
  reference: string;
  objects: OnboardingObject[];
  signedPdfDocs: { name: string; url: string }[];
  onGoToDashboard: () => void;
  tx: typeof T.de | typeof T.en;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(reference).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div>
      {/* Same header convention as every other step. */}
      <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[#7A8886]">
        {tx.step} {6} {tx.of} 6
      </p>
      <h1 className="mt-1.5 text-[21px] sm:text-2xl font-bold text-[#0F4C4A] tracking-tight">
        {tx.thankyouHeadline(name.split(" ")[0])}
      </h1>

      {/* Success banner. The prototype's shape: a single flat card with the
          confirmation line and reference number together, not a centered
          hero icon with its own badge below. */}
      <div className="mt-5 flex items-center gap-3.5 bg-[#FAF8F4] rounded-xl p-4">
        {/* Neel, 2026-10-16: a livelier confirmation -- the badge pops in,
            the check lands a beat later, and a halo ripples out twice from
            behind it. The halo then holds its last, transparent frame, so no
            teal edge shows around the resting badge. Under reduced motion
            nothing animates and the halo is not rendered. */}
        <div className="relative w-11 h-11 shrink-0">
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-[#2FB3AA]/30 motion-reduce:hidden motion-safe:animate-ping motion-safe:[animation-iteration-count:2] motion-safe:[animation-fill-mode:forwards]"
          />
          <div className="relative w-11 h-11 rounded-full bg-[#E8F5F3] flex items-center justify-center motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:fade-in motion-safe:duration-500">
            <CheckCircle2 className="w-[22px] h-[22px] text-[#2FB3AA] motion-safe:animate-in motion-safe:zoom-in-0 motion-safe:spin-in-45 motion-safe:duration-500 motion-safe:delay-300 motion-safe:fill-mode-both" />
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[14.5px] font-semibold text-[#06201F]">
            {tx.thankyouSub}
          </div>
          <div className="text-xs text-[#6F7D7B] mt-0.5">
            {tx.referenceLabel}{" "}
            <button
              onClick={handleCopy}
              title={tx.copyTitle}
              className="font-mono text-[#0F4C4A] hover:text-[#0B3A38]"
            >
              {reference}
            </button>
            {copied && (
              <span className="text-[#2FB3AA] ml-1.5 font-medium">
                {tx.copiedLabel}
              </span>
            )}
          </div>
        </div>
      </div>
      {objects.length > 1 && (
        <p className="text-xs text-[#9AA3A1] mt-2">
          {tx.thankyouObjects(objects.length)}
        </p>
      )}

      {/* Next steps — a flat divided list, like every other list on this
          flow, not individually-shadowed cards. */}
      <SectionLabel className="mt-7 mb-1">{tx.nextStepsLabel}</SectionLabel>
      <div>
        {tx.nextSteps.map((s) => (
          <div
            key={s.n}
            className="flex items-start gap-3 py-3 border-b border-[#EFEBE3] last:border-b-0"
          >
            <div className="w-6 h-6 rounded-full bg-[#E8F5F3] text-[#0F4C4A] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
              {s.n}
            </div>
            <div>
              <div className="font-semibold text-[#06201F] text-sm">
                {s.title}
              </div>
              <div className="text-xs text-[#6F7D7B] mt-0.5">{s.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Documents */}
      {signedPdfDocs.length > 0 && (
        <>
          <SectionLabel className="mt-7 mb-1">
            {tx.yourDocumentsLabel}
          </SectionLabel>
          <div>
            {signedPdfDocs.map((doc) => (
              <a
                key={doc.url}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 py-3 border-b border-[#EFEBE3] last:border-b-0 hover:opacity-80"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileText className="w-4 h-4 text-[#2FB3AA] shrink-0" />
                  <span className="text-sm font-medium text-[#06201F] truncate">
                    {doc.name}
                  </span>
                </div>
                <Download className="w-4 h-4 text-[#2FB3AA] shrink-0" />
              </a>
            ))}
          </div>
        </>
      )}

      <CardActions>
        <Button
          onClick={onGoToDashboard}
          className="bg-[#0F4C4A] hover:bg-[#0B3A38] text-white h-11 px-6 text-sm font-semibold rounded-full shadow-[0_8px_24px_rgba(15,76,74,0.18)]"
        >
          {tx.goToPlatform}
        </Button>
      </CardActions>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function Onboarding() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  // Keep the owner's app preference unchanged; only this flow is German-only.
  const { language } = useLanguage();
  const tx = pickDictionary(language);
  const sigPadRef = useRef<SignaturePad>(null);
  const signatureDraftRef = useRef<SignatureDraft | null>(null);
  const [platformDocs, setPlatformDocs] = useState<PlatformDoc[]>([]);

  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [property, setProperty] = useState<PropertyData | null>(null);
  /** Objects of a GROUPED invite, in printed slot order. Empty for a
      single-property invite, which the `properties` fetch below handles. */
  const [groupedRows, setGroupedRows] = useState<GroupedObjectRow[]>([]);
  const [step, setStep] = useState<number | "inbox">(1);
  const [editingOwner, setEditingOwner] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [signedPdfDocs, setSignedPdfDocs] = useState<
    { name: string; url: string }[]
  >([]);
  // Owner signature captured in step 4, reused in step 5
  const [ownerSignatureData, setOwnerSignatureData] = useState<string | null>(
    null
  );
  const [reference, setReference] = useState("");

  // Step 6 — set CoLiviq password before entering the owner portal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);
  const [pwdError, setPwdError] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    name: "",
    address: "",
    phone: "",
    bank_holder: "",
    iban: "",
    bic: "",
    payment_method: "sepa",
    card_expiry: "",
    card_cvc: "",
    agb_accepted: false,
    docs_accepted: false,
    withdrawal_policy_accepted: false,
    management_agreement_accepted: false,
  });

  useEffect(() => {
    // Use edge function to get signed URLs — storage bucket requires auth
    // so we proxy through the service-role function instead.
    supabase.functions.invoke("get-platform-docs").then(({ data }) => {
      if (data && typeof data === "object") {
        const docs: PlatformDoc[] = Object.entries(
          data as Record<string, string | null>
        ).map(([key, url]) => ({
          document_key: key as PlatformDoc["document_key"],
          file_url: url,
        }));
        setPlatformDocs(docs);
      }
    });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    if (!token) {
      setTokenError(tx.invalidLink);
      setLoading(false);
      return;
    }
    loadToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadToken() {
    setLoading(true);

    // Read the invite through a SECURITY DEFINER lookup rather than selecting
    // the table: owner_onboarding_tokens is no longer readable by anon, because
    // an open SELECT on it exposed every invited owner's contact and bank
    // details to anyone holding the shipped anon key (COL-296). The function
    // returns one row for an exact token match, and nulls the prefilled
    // personal/bank fields once the invite is completed or expired.
    const { data, error } = await supabase.rpc("get_owner_onboarding_token", {
      p_token: token,
    });

    // The lookup is set-returning, so supabase-js hands back an array; an
    // unknown or malformed token comes back empty rather than as an error.
    const row = (Array.isArray(data) ? data[0] : data) as TokenData | undefined;

    if (error || !row) {
      setTokenError(tx.linkExpiredOrInvalid);
      setLoading(false);
      return;
    }

    if (row.status === "completed") {
      setTokenError(tx.alreadyCompleted);
      setLoading(false);
      return;
    }

    if (new Date(row.expires_at) < new Date()) {
      setTokenError(tx.invitationExpired);
      setLoading(false);
      return;
    }

    setTokenData(row);

    // Which objects this invitation actually covers. Through the RPC, not the
    // child table directly: owner_onboarding_token_properties is not readable
    // by anon, for the same reason the token itself is not.
    // Cast: get_owner_onboarding_properties landed in 20261018160000 (#1908)
    // and src/lib/supabase-types.ts has not been regenerated since, so the
    // generated RPC union does not know the name or the row shape yet.
    // Regenerating types is its own change -- it rewrites a 10k-line file.
    const { data: groupedData, error: groupedError } = await (
      supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>
      ) => Promise<{ data: unknown; error: { message: string } | null }>
    )("get_owner_onboarding_properties", { p_token: token });

    // FAIL CLOSED. Swallowing this makes a grouped invite look like a single
    // one: the owner would review one property and one price, while
    // sign-vollmacht reads the child table successfully and stamps a mandate
    // over all of them. The documents were taught to read the group before
    // this screen was; a silent fallback here is the screen signing for
    // buildings it never showed.
    if (groupedError) {
      console.error(
        "[onboarding] grouped property lookup failed",
        groupedError
      );
      setTokenError(tx.groupedLookupFailed);
      setLoading(false);
      return;
    }
    setGroupedRows(
      Array.isArray(groupedData) ? (groupedData as GroupedObjectRow[]) : []
    );

    // Start with token prefilled data
    let bankHolder = row.prefilled_bank_holder ?? "";
    let iban = row.prefilled_iban ? formatIban(row.prefilled_iban) : "";
    let bic = row.prefilled_bic ?? "";

    // Look up the latest profile data for the invited email — always,
    // regardless of session. This ensures data edited via "Edit User" in admin
    // is reflected even before the owner has ever logged in (no auth session on
    // first onboarding visit). Anonymous visitors cannot read profiles
    // (COL-545), so this goes through a SECURITY DEFINER lookup keyed on the
    // live invite token; a failed lookup just leaves the token's own pre-fill.
    const { data: profileData } = await supabase.rpc(
      "get_owner_onboarding_profile",
      { p_token: token }
    );
    const profileRow = (
      Array.isArray(profileData) ? profileData[0] : profileData
    ) as
      | {
          user_id: string;
          name: string | null;
          phone: string | null;
          address: string | null;
        }
      | undefined;

    if (profileRow) {
      if (profileRow.name) {
        row.prefilled_name = profileRow.name;
        bankHolder = bankHolder || profileRow.name;
      }
      if (profileRow.phone) {
        row.prefilled_phone = profileRow.phone;
      }
      if (profileRow.address && !row.prefilled_address) {
        row.prefilled_address = profileRow.address;
      }

      // Also try bank details from property_owners using the profile id. RLS
      // lets only the signed-in owner (or staff) read this row.
      if (row.property_id) {
        const { data: ownerRow } = await supabase
          .from("property_owners")
          .select("bank_account_iban, bank_account_bic, bank_account_name")
          .eq("user_id", profileRow.user_id)
          .eq("property_id", row.property_id)
          .maybeSingle();

        if (ownerRow) {
          if (ownerRow.bank_account_iban)
            iban = formatIban(ownerRow.bank_account_iban);
          if (ownerRow.bank_account_bic) bic = ownerRow.bank_account_bic;
          if (ownerRow.bank_account_name)
            bankHolder = ownerRow.bank_account_name;
        }
      }
    }

    const resolvedName = row.prefilled_name ?? "";
    setForm((f) => ({
      ...f,
      name: resolvedName,
      phone: row.prefilled_phone ?? "",
      address: row.prefilled_address ?? "",
      bank_holder: bankHolder,
      iban,
      bic,
      // Pre-fill card holder from name as convenience
      // Auto-select SEPA if we have bank data, otherwise keep default
      payment_method: iban ? "sepa" : f.payment_method,
    }));

    if (row.property_id) {
      const { data: prop } = await supabase
        .from("properties")
        // Only columns `properties` actually has. rooms, living_area and
        // tenancy_status have never existed on this table, and PostgREST
        // rejects the WHOLE select on one unknown column (42703) -- so this
        // query returned nothing at all, every time, and `property` was never
        // set. Same defect as #1901/#1903, one layer up.
        .select(
          "id, title, address, city, zip_code, street_name, street_number"
        )
        .eq("id", row.property_id)
        .single();
      if (prop) setProperty(prop as unknown as PropertyData);
    } else if (profileRow) {
      // Fallback: look up property via the owner's profile → property_owners link
      const { data: ownerProp } = await supabase
        .from("property_owners")
        .select("property_id")
        .eq("user_id", profileRow.user_id)
        .limit(1)
        .maybeSingle();
      if (ownerProp?.property_id) {
        const { data: prop } = await supabase
          .from("properties")
          // See the note on the sibling query above: these three columns do
          // not exist, and one unknown column rejects the whole select.
          .select(
            "id, title, address, city, zip_code, street_name, street_number"
          )
          .eq("id", ownerProp.property_id)
          .single();
        if (prop) setProperty(prop as unknown as PropertyData);
      }
    }

    setLoading(false);
  }

  function updateForm(field: keyof FormState, value: string | boolean) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  /**
   * Step 5 (Auftrag) — place the binding order.
   *
   * Both network steps live here now that Vollmacht comes first: the
   * submission is created, then the signature captured in step 4 is applied
   * to the documents. Splitting them across the two steps would have stamped
   * a contract before the owner had seen the Widerrufsbelehrung.
   */
  async function handleSubmit() {
    if (!tokenData) return;

    // No signature means the owner reloaded past step 4; send them back rather
    // than placing an order with nothing to apply to the documents.
    if (!ownerSignatureData) {
      toast({ title: tx.step5SignatureRequired, variant: "destructive" });
      setStep(4);
      return;
    }

    // Already signed and sent — the owner navigated back to this step. Going
    // through again would re-stamp the documents and email the contracts a
    // second time, so just move forward.
    if (signedPdfDocs.length > 0) {
      setStep(6);
      return;
    }

    if (!isValidIban(form.iban)) {
      toast({ title: tx.ibanInvalid, variant: "destructive" });
      return;
    }

    setSubmitting(true);

    // Reuse an existing submission: the owner may be retrying after a failure
    // further down, and creating a second one would duplicate the order.
    let sid = submissionId;
    if (!sid) {
      const ref = generateReference();
      const { data, error } = await supabase.functions.invoke(
        "submit-onboarding",
        {
          body: {
            token_id: tokenData.id,
            token: tokenData.token,
            name: form.name,
            address: form.address,
            phone: form.phone,
            email: tokenData.email,
            bank_holder: form.bank_holder,
            iban: form.iban,
            bic: form.bic,
            payment_method: form.payment_method,
            agb_accepted: form.agb_accepted,
            withdrawal_policy_accepted: form.withdrawal_policy_accepted,
            management_agreement_accepted: form.management_agreement_accepted,
            reference_number: ref,
          },
        }
      );

      if (error || !data?.submission_id) {
        toast({
          title: tx.submissionFailed,
          description: error?.message ?? tx.submissionFailed,
          variant: "destructive",
        });
        setSubmitting(false);
        return;
      }

      sid = data.submission_id as string;
      setSubmissionId(sid);
      setReference(ref);
    }

    const { data: vollmachtData, error: vollmachtError } =
      await supabase.functions.invoke("sign-vollmacht", {
        body: {
          submission_id: sid,
          token: tokenData.token,
          signature_data: ownerSignatureData,
        },
      });

    if (vollmachtError) {
      // The submission stands, so a retry reuses it instead of ordering twice.
      toast({
        title: tx.submissionFailed,
        description: vollmachtError.message,
        variant: "destructive",
      });
      setSubmitting(false);
      return;
    }

    // sign-vollmacht returns signed contracts + Auftragsbestätigung + attached
    // templates. Drop entries with empty URLs (a storage upload that failed
    // server-side) so the thank-you page never offers a dead link.
    const signedList: { name: string; url: string }[] = Array.isArray(
      vollmachtData?.signed_pdf_urls
    )
      ? (
          vollmachtData.signed_pdf_urls as { name: string; url: string }[]
        ).filter((d) => d?.url && d?.name)
      : [];
    setSignedPdfDocs(signedList);

    // Provision the owner's CoLiviq account in the background with an internal
    // password so it exists the moment onboarding is signed — even if the owner
    // closes the tab before choosing their own password at the thank-you step.
    if (tokenData?.token) {
      supabase.functions
        .invoke("onboarding-create-account", {
          body: { submission_id: sid, token: tokenData.token },
        })
        .catch((e) =>
          console.warn(
            "[Onboarding] account provisioning failed (non-blocking):",
            e
          )
        );
    }

    // Non-blocking: the order is placed, so a mail failure must not strand the
    // owner here. URLs are passed through to avoid racing the DB write.
    supabase.functions
      .invoke("send-owner-contracts", {
        body: {
          submission_id: sid,
          token: tokenData.token,
          signed_pdf_urls: vollmachtData?.signed_pdf_urls ?? [],
        },
      })
      .catch((e) =>
        console.warn(
          "[Onboarding] send-owner-contracts failed (non-blocking):",
          e
        )
      );

    setStep(6);
    setSubmitting(false);
  }

  /**
   * Step 4 (Vollmacht) — confirm the documents.
   *
   * Deliberately makes NO network call. The signature stays in the browser and
   * is only applied when the owner places the binding order in step 5.
   */
  function handleVollmachtNext() {
    // Trim the surrounding whitespace so the signature isn't dwarfed by the
    // canvas padding when scaled into the PDF (otherwise it renders tiny).
    let sigData: string | null = null;
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      try {
        sigData = sigPadRef.current.getTrimmedCanvas().toDataURL("image/png");
      } catch {
        sigData = sigPadRef.current.toDataURL("image/png");
      }
    }

    if (!sigData) {
      toast({ title: tx.step5SignatureRequired, variant: "destructive" });
      return;
    }

    // Belt and braces: the CTA is disabled without this, but the guard is what
    // actually prevents an unconfirmed Vollmacht reaching the order step.
    if (!form.docs_accepted) {
      toast({ title: tx.vollmachtConsentRequired, variant: "destructive" });
      return;
    }

    setOwnerSignatureData(sigData);
    setStep(5);
  }

  async function handleGoToPlatform() {
    // If the owner already has an active session, go straight to the owner
    // portal. Owners live at /admin?tab=analytics — /dashboard is tenant-only
    // and bounces owners away.
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session) {
      window.location.href = "/admin?tab=analytics";
      return;
    }

    // No session — prompt the owner to set their CoLiviq password. Once set we
    // sign them in and open the portal directly (no email round-trip).
    setPwd("");
    setPwdConfirm("");
    setPwdError(null);
    setShowPasswordModal(true);
  }

  async function handleSetPassword() {
    if (!submissionId || !tokenData?.token) return;
    if (pwd.length < 8) {
      setPwdError(tx.pwdTooShort);
      return;
    }
    if (pwd !== pwdConfirm) {
      setPwdError(tx.pwdMismatch);
      return;
    }

    setSettingPassword(true);
    setPwdError(null);

    try {
      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        error?: string;
      }>("onboarding-set-password", {
        body: {
          submission_id: submissionId,
          token: tokenData.token,
          password: pwd,
        },
      });

      if (error || !data?.ok) {
        setPwdError(data?.error ?? tx.pwdSetFailed);
        setSettingPassword(false);
        return;
      }

      // Account is provisioned and the password is set — sign in and open the
      // owner portal. Full reload re-initialises auth with the new session.
      const ownerEmail = tokenData.email;
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: ownerEmail,
        password: pwd,
      });
      if (signInErr) {
        setPwdError(signInErr.message);
        setSettingPassword(false);
        return;
      }

      window.location.href = "/admin?tab=analytics";
    } catch (e) {
      console.warn("[Onboarding] set-password failed:", e);
      setPwdError(tx.pwdSetFailed);
      setSettingPassword(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-[#F7F3EC]">
        <div className="flex items-center mb-6">
          <BrandWordmark className="h-6 w-auto" />
        </div>
        <div className="w-8 h-8 border-2 border-[#0F4C4A] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400 mt-4">{tx.loadingLabel}</p>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-red-50/20 px-4">
        <div className="max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {tx.linkUnavailable}
            </h2>
            <p className="text-sm text-[#6F7D7B] leading-relaxed">
              {tokenError}
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-center">
            <p className="text-xs text-gray-400">
              Haben Sie Fragen? Schreiben Sie uns an{" "}
              <a
                href="mailto:info@coliviq.de"
                className="text-[#0F4C4A] font-medium hover:underline"
              >
                info@coliviq.de
              </a>
            </p>
          </div>
          <div className="flex items-center justify-center mt-6">
            <BrandWordmark className="h-4 w-auto opacity-60" />
          </div>
        </div>
      </div>
    );
  }

  const name = form.name || tokenData?.prefilled_name || "";
  const firstName = name.split(" ")[0] || "";
  // TODO(management-fee): temporary default until the per-apartment fee config
  // lands. Imported, not redeclared: this screen is where the owner ACCEPTS a
  // price, and the two Auftragsbestätigung generators fall back to the same
  // constant — a local copy here quoted 33 on screen against 49 on the signed
  // document for any invite with no fee configured.

  const feeAmount = tokenData?.prefilled_fee_amount ?? DEFAULT_MANAGEMENT_FEE;
  // No invented room: the Auftragsbestaetigung generators bill the literal
  // count (and refuse an unpriceable one), so defaulting to 1 here would show
  // a total the documents never print.
  const numRooms = tokenData?.prefilled_rooms ?? 0;

  // Correct the property the invite names. Token-gated and scoped to a
  // sole-owner building server-side (owner_update_onboarding_property); the
  // screen only decides whether to OFFER it, never whether it is allowed.
  const saveOnboardingProperty = async (patch: {
    title: string;
    street_name: string;
    street_number: string;
    zip_code: string;
    city: string;
  }): Promise<null | CorrectionRefusal> => {
    if (!token || !property) return "generic";
    // Cast: owner_update_onboarding_property landed in 20261019120000 and
    // src/lib/supabase-types.ts has not been regenerated since, so the
    // generated RPC union does not know it yet -- the same reason
    // get_owner_onboarding_properties is cast a few hundred lines above.
    const { error } = await (
      supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>
      ) => Promise<{ error: { message: string } | null }>
    )("owner_update_onboarding_property", {
      p_token: token,
      p_property_id: Number(property.id),
      p_title: patch.title,
      p_street_name: patch.street_name,
      p_street_number: patch.street_number,
      p_zip_code: patch.zip_code,
      p_city: patch.city,
    });
    if (error) {
      console.error("[onboarding] property update failed", error);
      return correctionRefusal(error.message);
    }

    // Re-read rather than patching blindly: `address` is recomposed
    // server-side, so the stored value is the only one guaranteed right.
    const { data: fresh } = await supabase
      .from("properties")
      .select("id, title, address, city, zip_code, street_name, street_number")
      .eq("id", Number(property.id))
      .maybeSingle();
    if (fresh) {
      setProperty(fresh as unknown as PropertyData);
      return null;
    }

    // The write succeeded but the read back did not (RLS, a dropped
    // connection). Reporting failure would be a lie, and leaving the old
    // values on screen would be a worse one.
    setProperty({
      ...property,
      ...applyCorrectionLocally(property, patch),
    });
    return null;
  };

  // Everything this invitation covers, priced. A grouped invite lists its
  // objects in owner_onboarding_token_properties; a single-property one has
  // no child rows and is built from the property the token names.
  //
  // Built here rather than in the step component so the screen and both
  // Auftragsbestaetigung generators price one and the same list -- the
  // generators read the same rows server-side.
  //
  // NOT objectsFromLegacyProperty for the single case: that helper wants
  // rooms / living_area / tenancy_status off the property row, and none of
  // those columns exist on `properties`. tenancyStatus stays null, which is
  // what all three surfaces already passed.
  //
  // Area and letting status now come from the TOKEN lookup instead, which
  // derives them from the apartment inside a definer function -- the page
  // still cannot read `apartments` itself. They are display-only:
  // `tenancyStatus` (which prices the object) is deliberately left null, so
  // surfacing a status cannot change what the owner is charged.
  const onboardingObjects: OnboardingObject[] =
    groupedRows.length > 0
      ? objectsFromGroupedRows(groupedRows, feeAmount)
      : [
          {
            slot: 1,
            propertyId: property?.id != null ? Number(property.id) : null,
            apartmentId: null,
            title: property?.title ?? null,
            address: property?.address ?? null,
            city: property?.city ?? null,
            zipCode: property?.zip_code ?? null,
            rooms: numRooms,
            livingArea: tokenData?.living_area ?? null,
            tenancyStatus: null,
            displayStatus: tokenData?.tenancy_status ?? null,
            feePerRoom: feeAmount,
          },
        ];

  if (step === "inbox") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-[#F7F3EC] px-4">
        <div className="flex items-center mb-8">
          <BrandWordmark className="h-6 w-auto" />
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-[#E3EFED] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-[#2FB3AA]" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {tx.mailOnTheWay}
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed">
            {tx.lang === "de"
              ? `Wir haben einen Anmeldelink an ${tokenData?.email ?? "Ihre E-Mail"} gesendet. Bitte prüfen Sie Ihren Posteingang.`
              : `We sent a login link to ${tokenData?.email ?? "your email"}. Please check your inbox to access your dashboard.`}
          </p>
        </div>
      </div>
    );
  }

  if (step === 6) {
    return (
      <>
        <Shell step={6} total={STEP_LABELS_DE.length} tx={tx}>
          <Step6ThankYou
            name={firstName || name}
            reference={reference}
            objects={onboardingObjects}
            signedPdfDocs={signedPdfDocs}
            onGoToDashboard={handleGoToPlatform}
            tx={tx}
          />
        </Shell>

        <Dialog
          open={showPasswordModal}
          onOpenChange={(open) => {
            // Block dismissal while the request is in flight.
            if (!settingPassword) setShowPasswordModal(open);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="w-12 h-12 rounded-2xl bg-[#E3EFED] border border-[#CFE4E2] flex items-center justify-center mb-2">
                <Lock className="w-6 h-6 text-[#0F4C4A]" />
              </div>
              <DialogTitle>{tx.pwdTitle}</DialogTitle>
              <DialogDescription>{tx.pwdSubtitle}</DialogDescription>
            </DialogHeader>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSetPassword();
              }}
              className="space-y-4 mt-2"
            >
              <div className="space-y-1.5">
                <Label htmlFor="onboarding-pwd">{tx.pwdLabel}</Label>
                <Input
                  id="onboarding-pwd"
                  type="password"
                  autoComplete="new-password"
                  placeholder={tx.pwdPlaceholder}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  disabled={settingPassword}
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="onboarding-pwd-confirm">
                  {tx.pwdConfirmLabel}
                </Label>
                <Input
                  id="onboarding-pwd-confirm"
                  type="password"
                  autoComplete="new-password"
                  placeholder={tx.pwdPlaceholder}
                  value={pwdConfirm}
                  onChange={(e) => setPwdConfirm(e.target.value)}
                  disabled={settingPassword}
                />
              </div>

              {pwdError && (
                <p className="text-sm text-red-500" role="alert">
                  {pwdError}
                </p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={settingPassword}
              >
                {settingPassword ? tx.pwdSettingUp : tx.pwdSubmit}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Shell
      step={step as number}
      total={STEP_LABELS_DE.length}
      hideChrome={step === 4 || step === 5}
      objectCount={onboardingObjects.length}
      onBack={
        step === 1
          ? () => navigate(-1)
          : () => setStep((s) => (typeof s === "number" ? s - 1 : 6))
      }
      tx={tx}
    >
      {step === 1 && (
        <Step1Welcome name={firstName} onNext={() => setStep(2)} tx={tx} />
      )}
      {step === 2 && (
        <Step2Details
          form={form}
          objects={onboardingObjects}
          onSaveProperty={saveOnboardingProperty}
          email={tokenData?.email ?? ""}
          property={property}
          editingOwner={editingOwner}
          setEditingOwner={setEditingOwner}
          onChange={updateForm}
          onNext={() => setStep(3)}
          tx={tx}
        />
      )}
      {step === 3 && (
        <Step3Services
          feeAmount={feeAmount}
          numRooms={numRooms}
          objects={onboardingObjects}
          docs={platformDocs}
          onNext={() => setStep(4)}
          tx={tx}
        />
      )}
      {/* Vollmacht (4) before Auftrag (5): the owner reads and signs the power
          of attorney, then places the binding order. The signature is held in
          the browser between the two, so nothing is transmitted and no
          contract exists before the Widerrufsbelehrung has been accepted. */}
      {step === 4 && (
        <Step5Vollmacht
          docs={platformDocs}
          form={form}
          property={property}
          objects={onboardingObjects}
          submissionId={submissionId}
          onboardingToken={tokenData?.token ?? null}
          ownerSignatureData={ownerSignatureData}
          signatureDraftRef={signatureDraftRef}
          step={step}
          total={STEP_LABELS_DE.length}
          sigPadRef={sigPadRef}
          onChange={updateForm}
          onSubmit={handleVollmachtNext}
          onBack={() => setStep(3)}
          onClose={() => navigate(-1)}
          submitting={false}
          tx={tx}
        />
      )}
      {step === 5 && (
        <Step4Contract
          form={form}
          property={property}
          feeAmount={feeAmount}
          numRooms={numRooms}
          objects={onboardingObjects}
          docs={platformDocs}
          onChange={updateForm}
          step={step}
          total={STEP_LABELS_DE.length}
          onBack={() => setStep(4)}
          onClose={() => navigate(-1)}
          onSubmit={handleSubmit}
          submitting={submitting}
          tx={tx}
        />
      )}
    </Shell>
  );
}
